import React, { useState, useEffect, useMemo } from 'react';
import { CompleteKundali, KundaliChartData, Language, PlanetPosition, PlanetName } from '../types/kundali';
import { getComprehensiveHouseCondition, ComprehensiveHouseAnalysis, getBusinessRecommendations } from '../utils/astrologyRules';
import { calculateShadbala, getBirthUtcDate, ZODIAC_SIGNS } from '../utils/vedicEngine';
import {
  getPlanetFullName,
  getPlanetShortCode,
  getZodiacName,
  getDignityName,
  getBeneficenceName,
  getAvasthaName,
  getAvasthaEffect,
  translateRoleDescription,
  getHouseCategoryName,
  getHouseSanskritName,
  getHouseTitle,
  getHouseDescription,
  getHouseThemes,
  getVerdictBadgeName,
  getClimateBadgeName,
  getAvRatingName,
  translateHouseStrengthSummary,
  translateAvAnalysis,
  translateAvTransit,
  translateGocharVerdict,
  translateTimingGuidance,
  translateVedicImpact,
  translateAspectImpact,
  translateAshtakvargaSynergy
} from '../utils/language';
import {
  Sparkles,
  Shield,
  Compass,
  Star,
  Eye,
  ArrowRightLeft,
  Info,
  ChevronRight,
  Award,
  BarChart2,
  RefreshCw,
  Activity,
  Radio,
  Zap,
  ChevronDown,
  ChevronUp,
  Briefcase,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Layers,
  ShieldCheck,
  BookOpen,
  ArrowUp,
  ArrowDown,
  Calendar,
  EyeOff,
  ArrowRightCircle,
  TrendingUp
} from 'lucide-react';
import { formatDate } from '../utils/dateUtils';
import { UnifiedBhavaConditionAssessmentView } from './UnifiedBhavaConditionAssessmentView';
import { UnifiedRemedialEngineView } from './UnifiedRemedialEngineView';
import { getFunctionalNature } from '../utils/paeEngine';
import {
  getEnhancedPlanetaryDignityList,
  EnhancedPlanetaryDignity,
  getPanchadhaMaitriCompound,
  getCompoundRelationshipHindi
} from '../utils/planetaryDignityEngine';
import {
  PopoverWrapper,
  PlanetHouseInfluenceHoverCard,
  DignityHoverCard,
  ShadbalaHoverCard,
  NatureHoverCard,
  NakshatraHoverCard,
  BhaveshaRelationshipHoverCard,
  getDignityThemeStyles
} from './PlanetaryHoverPopover';
import {
  calculatePlanetaryTransitPeriod,
  PlanetaryTransitPeriod,
  calculateNextTransitsForSign,
  HouseNextTransitInfo,
  UpcomingTransitIngress
} from '../utils/gocharPeriodEngine';

// Helpers for House Dynamics Table
const getNaturalRelationship = (lagnaLord: string, planet: string) => {
  if (planet === 'Ascendant' || planet === 'Rahu' || planet === 'Ketu') return 'Neutral';
  if (lagnaLord === planet) return 'Self / Root';

  const friendships: Record<string, { friends: string[], enemies: string[] }> = {
    Sun: { friends: ['Moon', 'Mars', 'Jupiter'], enemies: ['Venus', 'Saturn'] },
    Moon: { friends: ['Sun', 'Mercury'], enemies: [] },
    Mars: { friends: ['Sun', 'Moon', 'Jupiter'], enemies: ['Mercury'] },
    Mercury: { friends: ['Sun', 'Venus'], enemies: ['Moon'] },
    Jupiter: { friends: ['Sun', 'Moon', 'Mars'], enemies: ['Mercury', 'Venus'] },
    Venus: { friends: ['Mercury', 'Saturn'], enemies: ['Sun', 'Moon'] },
    Saturn: { friends: ['Mercury', 'Venus'], enemies: ['Sun', 'Moon', 'Mars'] }
  };

  const lordRules = friendships[lagnaLord];
  if (!lordRules) return 'Neutral';
  if (lordRules.friends.includes(planet)) return 'Friend';
  if (lordRules.enemies.includes(planet)) return 'Enemy';
  return 'Neutral';
};

// Classical House Karakas (Naisargika Karakatva)
const getHouseKarakas = (house: number): string[] => {
  const karakaMap: Record<number, string[]> = {
    1: ['Sun', 'Mars'],
    2: ['Jupiter', 'Mercury'],
    3: ['Mars'],
    4: ['Moon', 'Venus', 'Mars'],
    5: ['Jupiter', 'Mercury'],
    6: ['Mars', 'Saturn'],
    7: ['Venus', 'Jupiter'],
    8: ['Saturn'],
    9: ['Jupiter', 'Sun'],
    10: ['Sun', 'Mercury', 'Saturn', 'Jupiter'],
    11: ['Jupiter'],
    12: ['Saturn', 'Ketu', 'Venus']
  };
  return karakaMap[house] || [];
};

// Five-Fold Compound Friendship (Panchadha Maitri) with the House Lord (Bhavesha)
const getBhaveshaRelationship = (
  planetName: string,
  signLordName: string,
  planetHouse: number,
  signLordHouse: number
) => {
  if (planetName === signLordName) {
    return {
      compound: 'Own House',
      compoundEn: 'Lord in Own House',
      compoundHi: 'स्वक्षेत्री भावेश (स्वामी)',
      natural: 'Self',
      naturalHi: 'स्वयं',
      temporal: 'Self',
      temporalHi: 'स्वयं',
      isLord: true
    };
  }
  if (planetName === 'Ascendant' || planetName === 'Rahu' || planetName === 'Ketu') {
    return {
      compound: 'Neutral',
      compoundEn: 'Karmic / Neutral',
      compoundHi: 'छाया / सम',
      natural: 'Neutral',
      naturalHi: 'सम',
      temporal: 'Neutral',
      temporalHi: 'सम',
      isLord: false
    };
  }

  const naturalRel = getNaturalRelationship(signLordName, planetName) as 'Friend' | 'Neutral' | 'Enemy';
  const naturalHi = naturalRel === 'Friend' ? 'मित्र' : naturalRel === 'Enemy' ? 'शत्रु' : 'सम';
  
  // Temporal friendship (Tatkalika Mitra): Houses 2, 3, 4, 10, 11, 12 from signLordHouse
  const houseDistance = ((planetHouse - signLordHouse + 12) % 12) + 1;
  const isTemporalFriend = [2, 3, 4, 10, 11, 12].includes(houseDistance);
  const temporalRel: 'Friend' | 'Enemy' = isTemporalFriend ? 'Friend' : 'Enemy';
  const temporalHi = isTemporalFriend ? 'तात्कालिक मित्र' : 'तात्कालिक शत्रु';
  
  const compound = getPanchadhaMaitriCompound(naturalRel, temporalRel);
  const compoundHi = getCompoundRelationshipHindi(compound);
  
  return {
    compound,
    compoundEn: compound,
    compoundHi,
    natural: naturalRel,
    naturalHi,
    temporal: temporalRel,
    temporalHi,
    isLord: false
  };
};

const getExactDignity = (planet: string, signIndex: number, deg: number) => {
  if (planet === 'Ascendant') return 'Base Ascendant';
  
  const EXALTATIONS: { [key: string]: number } = { Sun: 0, Moon: 1, Mars: 9, Mercury: 5, Jupiter: 3, Venus: 11, Saturn: 6, Rahu: 1, Ketu: 7 };
  const DEBILITATIONS: { [key: string]: number } = { Sun: 6, Moon: 7, Mars: 3, Mercury: 11, Jupiter: 9, Venus: 5, Saturn: 0, Rahu: 7, Ketu: 1 };
  
  if (EXALTATIONS[planet] === signIndex) return 'Exalted (Uccha)';
  if (DEBILITATIONS[planet] === signIndex) return 'Debilitated (Neecha)';

  if (planet === 'Sun' && signIndex === 4 && deg <= 20) return 'Moolatrikona';
  if (planet === 'Moon' && signIndex === 1 && deg > 3 && deg <= 20) return 'Moolatrikona';
  if (planet === 'Mars' && signIndex === 0 && deg <= 12) return 'Moolatrikona';
  if (planet === 'Mercury' && signIndex === 5 && deg > 15 && deg <= 20) return 'Moolatrikona';
  if (planet === 'Jupiter' && signIndex === 8 && deg <= 10) return 'Moolatrikona';
  if (planet === 'Venus' && signIndex === 6 && deg <= 15) return 'Moolatrikona';
  if (planet === 'Saturn' && signIndex === 10 && deg <= 20) return 'Moolatrikona';

  const ownSigns: Record<string, number[]> = {
    Sun: [4], Moon: [3], Mars: [0, 7], Mercury: [2, 5], Jupiter: [8, 11], Venus: [1, 6], Saturn: [9, 10]
  };
  
  if (ownSigns[planet]?.includes(signIndex)) return 'Own House (Swakshetra)';

  const ZODIAC_LORDS = ['Mars', 'Venus', 'Mercury', 'Moon', 'Sun', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Saturn', 'Jupiter'];
  const signLord = ZODIAC_LORDS[signIndex];

  if (planet === 'Rahu' || planet === 'Ketu') return 'Neutral Sign';

  const rel = getNaturalRelationship(planet, signLord);
  if (rel === 'Friend') return 'Friendly Sign (Mitra)';
  if (rel === 'Enemy') return 'Enemy Sign (Shatru)';
  return 'Neutral Sign (Sama)';
};

// Classical Vedic astrological results for all 9 planets across the 12 Bhavas
const PLANET_HOUSE_RESULTS: Record<string, Record<number, { en: string; hi: string }>> = {
  Sun: {
    1: {
      en: 'Bestows commanding authority, noble self-esteem, regal charisma, and robust vital energy. Tends toward leadership and independent initiative; requires conscious humility.',
      hi: 'प्रभावी व्यक्तित्व, आत्म-सम्मान, नेतृत्व क्षमता और उच्च जीवन ऊर्जा प्रदान करता है। स्वतंत्र निर्णय लेने में कुशल, अहंकार से बचाव आवश्यक।'
    },
    2: {
      en: 'Fosters authoritative speech, inheritance of family pride, and financial dignity. Wealth is attained through government, administration, or family enterprise.',
      hi: 'तेजस्वी वाणी, पारिवारिक प्रतिष्ठा और स्वाभिमान प्रदान करता है। प्रशासन, सत्ता या पैतृक उपक्रमों से धनोपार्जन में सहायक।'
    },
    3: {
      en: 'Endows intense courage (Parakrama), adventurous enterprise, valor in communications, and successful initiative through independent self-effort.',
      hi: 'असीम पराक्रम, निर्भीक पहल, प्रभावी संचार और स्वप्रयासों से उपलब्धियां दिलाता है। छोटी यात्राओं और साहसिक कार्यों में सफल।'
    },
    4: {
      en: 'Focuses domestic life on honor, heritage property, and high principles. Grants stately residence; calls for warmth and patience in family relations.',
      hi: 'पारिवारिक मान-मर्यादा, अचल संपत्ति और कुल गौरव को सुदृढ़ करता है। भव्य आवास की चाह, घरेलू मामलों में सौम्यता बनाए रखना हितकर।'
    },
    5: {
      en: 'Confers brilliant analytical intellect, strategic governance acumen, creative authority, and pride in progeny. Auspicious for speculative and advisory leadership.',
      hi: 'तीक्ष्ण मेधा, रणनीतिक दूरदर्शिता, परामर्श क्षमता और संतान से गौरव दिलाता है। बौद्धिक और प्रशासनिक कार्यों में विशेष सफलता।'
    },
    6: {
      en: 'Acts as Shatruhanta (destroyer of obstacles and rivals). Grants formidable immunity, victory in competitive examinations, litigation, and career hurdles.',
      hi: 'शत्रुहंता योग निर्मित करता है। प्रतियोगिताओं, मुकदमों और बाधाओं पर विजय दिलाता है। उत्तम रोग-प्रतिरोधक क्षमता प्रदान करता है।'
    },
    7: {
      en: 'Brings an ambitious, principled, and influential life partner or business associates. Encourages shared leadership; rewards mutual compromise in marriage.',
      hi: 'प्रतिष्ठित, स्वाभिमानी और प्रभावशाली जीवनसाथी व व्यावसायिक सहयोगी दिलाता है। साझेदारी में आपसी सामंजस्य बनाए रखना आवश्यक।'
    },
    8: {
      en: 'Awakens deep research insight, occult understanding, interest in unrevealed knowledge, and transformative regeneration. Financial gains through legacy.',
      hi: 'गूढ़ विद्याओं, गहन शोध, पैतृक संपत्ति और जीवन के रहस्यों को समझने की क्षमता देता है। मनोवैज्ञानिक रूपांतरण में सहायक।'
    },
    9: {
      en: 'Blesses with dharmic wisdom, guidance from mentors, philosophical fortune (Bhagya), high ethics, and auspicious pilgrimages or higher education.',
      hi: 'धार्मिक निष्ठा, गुरु कृपा, भाग्य वृद्धि और उच्च दार्शनिक ज्ञान का वरदान देता है। तीर्थाटन और सात्विक कर्मों में रुचि।'
    },
    10: {
      en: 'Attains Digbala (Directional Strength). Confers executive authority, government recognition, career zenith, renowned public stature, and professional renown.',
      hi: 'दिग्बली होकर सर्वोच्च प्रशासनिक अधिकार, सरकारी मान-सम्मान, कार्यक्षेत्र में कीर्ति और उच्च पद प्रदान करता है।'
    },
    11: {
      en: 'Generates substantial recurring inflows through executive network, influential patronage, and complete realization of major lifelong ambitions.',
      hi: 'प्रभावशाली संपर्कों, शासन-सत्ता और बड़े उपक्रमों से प्रचुर आर्थिक लाभ कराता है। महत्त्वाकांक्षाओं की पूर्णता में अत्यंत सहायक।'
    },
    12: {
      en: 'Inspires spiritual self-reflection, generous philanthropy, success in foreign lands or government institutions, and detachment from worldly pretension.',
      hi: 'आध्यात्मिक अंतर्मुखता, परोपकार, विदेश संबंधों व दूरस्थ संस्थानों में सफलता प्रदान करता है। सात्विक व्यय की ओर प्रेरित।'
    }
  },
  Moon: {
    1: {
      en: 'Endows magnetic charm, emotional sensitivity, intuitive adaptability, radiant popularity, and high public rapport.',
      hi: 'आकर्षक सौम्य व्यक्तित्व, गहन संवेदनशीलता, भावनात्मक बुद्धिमत्ता और जनता के बीच सहज लोकप्रियता प्रदान करता है।'
    },
    2: {
      en: 'Brings sweet and persuasive speech, liquid financial resources, devotion to family nourishment, and fluctuating yet abundant wealth.',
      hi: 'मधुर वाणी, तरल वित्तीय संसाधन, परिवार के प्रति गहरा लगाव और भोजन व सुरुचिपूर्ण जीवनशैली का सुख देता है।'
    },
    3: {
      en: 'Inspires poetic creativity, artistic communications, fond bonding with younger siblings, and enjoyable short scenic journeys.',
      hi: 'कलात्मक कल्पनाशीलता, सम्मोहक संवाद, अनुजों से स्नेह और सुखद यात्राओं से मन को प्रसन्नता प्रदान करता है।'
    },
    4: {
      en: 'Attains Digbala. Bestows profound maternal happiness, luxurious and serene domestic sanctuary, vehicles, and enduring inner peace (Sukha).',
      hi: 'दिग्बली होकर परम मातृसुख, सुंदर व शांतिपूर्ण गृह वातावरण, वाहन सुख और आंतरिक मानसिक तृप्ति प्रदान करता है।'
    },
    5: {
      en: 'Blesses with vivid creative imagination, emotional depth in romantic life, intuitive learning, and loving devotion to children.',
      hi: 'रचनात्मक प्रतिभा, भावुक व निष्ठावान प्रेम, त्वरित ग्रहण क्षमता और सुयोग्य संतान का सुख प्रदान करता है।'
    },
    6: {
      en: 'Cultivates empathetic healing capacities and dedication to service; calls for conscious emotional resilience under daily pressure.',
      hi: 'दूसरों की सेवा व सहायता की गहरी भावना, रोग-निवारक समझ; तनावपूर्ण परिस्थितियों में मानसिक संतुलन बनाए रखना आवश्यक।'
    },
    7: {
      en: 'Brings an affectionate, empathetic, and culturally refined partner. Enhances public relations and harmonious social commerce.',
      hi: 'सौम्य, संवेदनशील और सुरुचिपूर्ण जीवनसाथी दिलाता है। जनसंपर्क और व्यापारिक अनुबंधों में मधुरता व सफलता।'
    },
    8: {
      en: 'Awakens acute psychic intuition, emotional depth, psychological resilience, and curiosity in hidden or metaphysical knowledge.',
      hi: 'तीव्र अंतर्ज्ञान, मनोवैज्ञानिक गहराई, गूढ़ रहस्यों के प्रति आकर्षण और गहरे भावनात्मक अनुभव प्रदान करता है।'
    },
    9: {
      en: 'Blesses with serene spiritual faith, auspicious fortune through maternal blessings, joy in pilgrimages, and benevolent wisdom.',
      hi: 'मातृ कृपा से भाग्योदय, पवित्र तीर्थयात्राओं में आनंद, धार्मिक निष्ठा और जीवन के प्रति उदार दार्शनिक दृष्टिकोण।'
    },
    10: {
      en: 'Confers wide public visibility, career success connected with masses, hospitality, public welfare, and adaptable leadership.',
      hi: 'जनता से जुड़े कार्यों, लोककल्याण, जनसेवा व मीडिया में सम्माननीय पहचान और सामाजिक लोकप्रियता प्रदान करता है।'
    },
    11: {
      en: 'Draws supportive emotional friendships, prosperous community networks, steady income streams, and joy in fulfilled desires.',
      hi: 'सहयोगी मित्रों का मंडल, सामाजिक दायरों से निरंतर लाभ, आर्थिक संवृद्धि और आकांक्षाओं की सहज पूर्ति कराता है।'
    },
    12: {
      en: 'Fosters rich subconscious contemplation, peaceful solitude, interest in meditative arts, and positive fortune in distant lands.',
      hi: 'गहन चिंतन, एकांत में मानसिक शांति, ध्यान-साधना में रुचि और विदेश या दूरस्थ स्थानों में अनुकूल वातावरण।'
    }
  },
  Mars: {
    1: {
      en: 'Instills fearless courage, athletic drive, dynamic ambition, and swift initiative. Tends toward direct action; directs fiery energy constructively.',
      hi: 'अदम्य साहस, शारीरिक चुस्ती, त्वरित निर्णय और नेतृत्व की ऊर्जा देता है। आवेश पर नियंत्रण रखने से ऊर्जा अत्यंत फलदायी होती है।'
    },
    2: {
      en: 'Brings bold, frank speech and energetic ambition in building family wealth and assets. Protects family resources with fierce loyalty.',
      hi: 'स्पष्टवादी वाणी, संपत्ति निर्माण में आक्रामक संकल्प और पारिवारिक सुरक्षा के प्रति सजगता। वित्तीय मामलों में साहसी।'
    },
    3: {
      en: 'Supreme house for Mars. Bestows peerless courage (Parakrama), sports/engineering aptitude, fearless initiative, and decisive willpower.',
      hi: 'मंगल का परम कारक भाव। अद्वितीय पराक्रम, तकनीकी/यांत्रिक कौशल, निडरता और दृढ़ इच्छाशक्ति से बड़ी सफलताएं दिलाता है।'
    },
    4: {
      en: 'Stimulates real estate investments, land ownership, and protective instincts for home. Channel fiery emotions constructively for family harmony.',
      hi: 'भूमि, भवन, अचल संपत्ति और निर्माण कार्यों में सफलता दिलाता है। पारिवारिक शांति हेतु संवाद में कोमलता लाभप्रद।'
    },
    5: {
      en: 'Grants sharp technical and competitive intellect, passionate creative drive, and sports acumen; advises patience with children.',
      hi: 'तीक्ष्ण तकनीकी बुद्धि, प्रतिस्पर्धी परीक्षाओं में प्रखरता और साहसिक निर्णय। संतान के साथ धैर्यपूर्ण व्यवहार उत्तम।'
    },
    6: {
      en: 'Formidable placement for Mars. Completely conquers adversaries, ensures legal victories, and provides relentless endurance against challenges.',
      hi: 'शत्रुओं, मुकदमों और ऋणों पर पूर्ण विजय दिलाता है। कठिन परिस्थितियों में अद्भुत सहनशक्ति और जुझारूपन देता है।'
    },
    7: {
      en: 'Brings a dynamic, ambitious, and energetic spouse or business partner. Requires mutual respect and patient communication in partnerships.',
      hi: 'ऊर्जावान, महत्वाकांक्षी और स्वाभिमानी जीवनसाथी। वैवाहिक व व्यावसायिक साझेदारी में आपसी आदर व धैर्य आवश्यक।'
    },
    8: {
      en: 'Provides immense capacity for crisis recovery, emergency endurance, deep investigative research, and sudden transformative breakthroughs.',
      hi: 'संकटों से उबरने की असाधारण क्षमता, शल्यक्रिया/अनुसंधान में सफलता और जीवन के गहरे बदलावों को सहने का बल देता है।'
    },
    9: {
      en: 'Inspires courageous defense of moral principles, adventurous exploratory travels, and fearless pursuit of ideological convictions.',
      hi: 'सिद्धांतों की रक्षा में निर्भीकता, साहसिक यात्राएं और अपने विश्वास पर अडिग रहने का आत्मबल प्रदान करता है।'
    },
    10: {
      en: 'Attains Digbala (Zenith). Confers supreme executive authority, technical/defense/corporate leadership, and commanding professional stature.',
      hi: 'दिग्बली होकर पुलिस, सेना, इंजीनियरिंग, प्रशासन या बड़े उद्योगों में शीर्ष नेतृत्व, कीर्ति और अधिकार दिलाता है।'
    },
    11: {
      en: 'Generates robust material profits through bold enterprise, high-level connections, real estate gains, and triumphant realization of goals.',
      hi: 'साहसिक उपक्रमों, संपत्तियों और बड़े संकल्पों से प्रचुर आर्थिक लाभ कराता है। मित्रों में प्रभावशाली स्थिति।'
    },
    12: {
      en: 'Directs physical drive toward selfless discipline, foreign ventures, technical research, or martial arts behind the scenes.',
      hi: 'विदेश में तकनीकी या स्वतंत्र उपक्रमों में सफलता, आध्यात्मिक अनुशासन और गुप्त विरोधियों से सुरक्षा प्रदान करता है।'
    }
  },
  Mercury: {
    1: {
      en: 'Attains Digbala. Bestows intellectual brilliance, youthful charm, articulate wit, diplomatic skill, and analytical versatility.',
      hi: 'दिग्बली होकर प्रखर बुद्धि, हाजिरजवाबी, युवा जैसी स्फूर्ति, उत्कृष्ट संवाद शैली और बहुमुखी प्रतिभा का धनी बनाता है।'
    },
    2: {
      en: 'Grants eloquent speech, commercial calculation, financial accounting intellect, and steady wealth through consulting and trade.',
      hi: 'मधुर व तार्किक वाणी, वाणिज्यिक बुद्धि, वित्तीय प्रबंधन में निपुणता और व्यापार/परामर्श से धनोपार्जन कराता है।'
    },
    3: {
      en: 'Excels in writing, digital media, publication, sales, IT innovation, and witty communication. Fosters collaborative sibling relationships.',
      hi: 'लेखन, आईटी, डिजिटल मीडिया, संचार और व्यापार में निपुणता। भाई-बहनों से दोस्ताना और बौद्धिक तालमेल।'
    },
    4: {
      en: 'Fosters scholastic excellence, intellectual home environment, study libraries, and analytical serenity of mind.',
      hi: 'उच्च शैक्षणिक योग्यता, बौद्धिक व सुरुचिपूर्ण गृह वातावरण, पुस्तकों का संग्रह और विचारशील मानसिक शांति देता है।'
    },
    5: {
      en: 'Supreme analytical and advisory genius. Grants mathematical mastery, logic, speculative intelligence, and joyful mentoring abilities.',
      hi: 'असाधारण तार्किक मेधा, गणितीय व सलाहकार कौशल, रचनात्मक लेखन और संतान की उत्तम शिक्षा में सहायक।'
    },
    6: {
      en: 'Masters analytical problem-solving, commercial audits, competitive debate, legal documentation, and detail-oriented operations.',
      hi: 'विश्लेषणात्मक समाधान, हिसाब-किताब, कानूनी दस्तावेज और जटिल समस्याओं को सुलझाने में असाधारण दक्षता देता है।'
    },
    7: {
      en: 'Brings an intelligent, witty, and business-minded spouse. Fosters profitable trade alliances, diplomacy, and commercial negotiations.',
      hi: 'बुद्धिमान, हंसमुख और व्यापारिक सूझबूझ वाला जीवनसाथी। व्यापारिक साझेदारी और समझौतों में विशेष सफलता।'
    },
    8: {
      en: 'Drives deep analytical investigations, data research, occult mathematics, and unravelling obscure or esoteric systems.',
      hi: 'डेटा विश्लेषण, गहन शोध, गूढ़ विद्याओं और रहस्यों की पड़ताल में गहरी बौद्धिक रुचि व सफलता प्रदान करता है।'
    },
    9: {
      en: 'Promotes higher academic scholarship, publishing, philosophical discourse, legal knowledge, and learning from enlightened preceptors.',
      hi: 'उच्च शिक्षा, प्रकाशन, दार्शनिक संवाद और धर्मग्रंथों के बौद्धिक अध्ययन में गहरी रुचि व सम्मान दिलाता है।'
    },
    10: {
      en: 'Confers outstanding commercial and executive success, media recognition, advisory leadership, and analytical prestige in career.',
      hi: 'व्यापार, संचार, सलाहकार, मीडिया या प्रबंधन के क्षेत्र में विशिष्ट सफलता, प्रतिष्ठा और व्यावसायिक प्रतिष्ठा देता है।'
    },
    11: {
      en: 'Opens multiple intellectual income channels, networking with commercial elites, and easy realization of business aspirations.',
      hi: 'व्यापारिक नेटवर्क से आय के कई स्रोत, बौद्धिक मित्रों का साथ और व्यावसायिक योजनाओं से प्रचुर आर्थिक लाभ।'
    },
    12: {
      en: 'Inclines toward abstract philosophy, analytical research in foreign countries, intellectual solitude, and foreign contracts.',
      hi: 'अमूर्त चिंतन, विदेशी व्यापार या संस्थानों में अनुसंधान, बौद्धिक एकांत और आध्यात्मिक ग्रंथों के अध्ययन में सहायक।'
    }
  },
  Jupiter: {
    1: {
      en: 'Attains Digbala. Imparts supreme wisdom, noble magnetic presence, righteous conduct, divine protection, and life-long prosperity.',
      hi: 'दिग्बली होकर देवगुरु की पूर्ण कृपा, सात्विक चरित्र, जीवन भर सुरक्षा कवच, प्रतिष्ठा और आत्मिक संतोष प्रदान करता है।'
    },
    2: {
      en: 'Bestows truthful, inspiring speech, prosperous treasury, ethical financial investments, and deep spiritual heritage in family.',
      hi: 'सत्यवादी व ज्ञानवर्धक वाणी, प्रचुर धन-धान्य, वित्तीय समृद्धि और सुसंस्कृत पारिवारिक परंपरा का वरदान देता है।'
    },
    3: {
      en: 'Guides advisory and philosophical writing, uplifting communications, respectful relations with siblings, and noble initiatives.',
      hi: 'प्रेरणादायी संवाद, नीतिगत लेखन, भाई-बहनों से आदरपूर्ण संबंध और सद्कार्यों की पहल में सफलता।'
    },
    4: {
      en: 'Blesses with a spacious, auspicious home, scholarly parents, profound peace of mind, high morality, and blessings of preceptors.',
      hi: 'भव्य व पवित्र आवास, संस्कारी माता-पिता, मानसिक शांति, उच्च विद्या और पारिवारिक सुख-समृद्धि देता है।'
    },
    5: {
      en: 'Supreme placement for Jupiter. Grants extraordinary wisdom (Dhi), spiritual merit (Purva Punya), noble progeny, and advisory genius.',
      hi: 'गुरु का परम फलदायी भाव। असाधारण विवेक, पूर्वजन्मों के शुभ कर्म, सुयोग्य व संस्कारी संतान और ज्ञान का वरदान।'
    },
    6: {
      en: 'Overcomes conflicts through diplomacy, ethical forgiveness, and healing wisdom. Protects against hostile disputes and health afflictions.',
      hi: 'शांति, क्षमा और नीति द्वारा विरोधियों को शांत करता है। सेवाभाव, चिकित्सा और कठिन परिस्थितियों से सुरक्षा देता है।'
    },
    7: {
      en: 'Brings a virtuous, noble, highly educated life partner. Ensures auspicious marital harmony, ethical trade, and respected public treaties.',
      hi: 'सद्गुणी, सुशिक्षित और कुलीन जीवनसाथी। दांपत्य जीवन में पवित्रता, सुख और व्यापारिक साझेदारियों में समृद्धि।'
    },
    8: {
      en: 'Endows deep esoteric wisdom, long lifespan (Ayush), unearned financial inheritances, and graceful, enlightened inner transformation.',
      hi: 'दीर्घायु, गूढ़ आध्यात्मिक ज्ञान, पैतृक लाभ और जीवन के गहन मोड़ों पर ईश्वरीय मार्गदर्शन व रूपांतरण देता है।'
    },
    9: {
      en: 'Supreme house of Dharma and Bhagya. Showering supreme fortune, spiritual preceptors, noble deeds, and divine grace across life.',
      hi: 'गुरु का स्व-कारक भाव। परम भाग्य, गुरुजनों का आशीर्वाद, तीर्थाटन और धर्म परायणता से जीवन में सब मंगलमय करता है।'
    },
    10: {
      en: 'Elevates to high ethical governance, institutional mentorship, corporate or judicial authority, and unblemished public honor.',
      hi: 'प्रशासन, न्याय, शिक्षा, परामर्श या धार्मिक संस्थानों में शीर्ष सम्मान, निष्कलंक प्रतिष्ठा और अधिकार दिलाता है।'
    },
    11: {
      en: 'Magnifies financial wealth, virtuous and enlightened friendships, abundant liquid gains, and complete fruition of all high desires.',
      hi: 'अपार धन लाभ, सज्जनों की संगति, आर्थिक समृद्धि और जीवन के समस्त बड़े लक्ष्यों की सहज पूर्ति कराता है।'
    },
    12: {
      en: 'Supreme Moksha indicator. Guides toward spiritual enlightenment, profound philanthropic charity, inner serenity, and divine grace in solitude.',
      hi: 'मोक्ष कारक। आत्म-साक्षात्कार, परोपकारी दान, अंतर्मुखी शांति और एकांत में ईश्वरीय चेतना का अनुभव कराता है।'
    }
  },
  Venus: {
    1: {
      en: 'Endows graceful physical beauty, refined aesthetic taste, artistic charisma, captivating social popularity, and romantic allure.',
      hi: 'मनोहर रूप-सौंदर्य, सुरुचिपूर्ण दृष्टिकोण, कलात्मक आकर्षण और समाज में सहज लोकप्रियता व आकर्षण प्रदान करता है।'
    },
    2: {
      en: 'Confers melodious voice, sweet persuasive speech, affluent jewelry and wealth holdings, and luxurious gourmet dining experiences.',
      hi: 'मधुर गायन/भाषण शैली, आभूषण व संपदा का संचय, और सुरुचिपूर्ण व्यंजनों व विलासिता का सुख देता है।'
    },
    3: {
      en: 'Inspires poetic arts, theatre, design, fine communications, harmonious bonding with siblings, and elegant short travels.',
      hi: 'संगीत, कला, नाट्य, डिजाइन में प्रतिभा, भाई-बहनों से मधुर संबंध और सुखद कलात्मक यात्राओं का योग।'
    },
    4: {
      en: 'Attains Digbala. Grants elite residences, luxury vehicles, lavish interior elegance, affectionate maternal joy, and domestic comfort.',
      hi: 'दिग्बली होकर भव्य गृह, लग्जरी वाहन, सुरुचिपूर्ण जीवनशैली, मातृस्नेह और संपूर्ण भौतिक सुख-सुविधाएं देता है।'
    },
    5: {
      en: 'Awakens artistic flair, romantic fulfillment, creative writing, refined entertainment ventures, and charming, creative children.',
      hi: 'रोमांटिक जीवन में आनंद, कला-साहित्य में सृजनशीलता, और सुंदर व संस्कारी संतान का सुख प्रदान करता है।'
    },
    6: {
      en: 'Overcomes workplace friction through personal charm and diplomacy; calls for moderation in health regimens and sweet habits.',
      hi: 'सौम्यता और कूटनीति से विवादों को शांत करता है। कार्यस्थल पर सौहार्द, खान-पान में संतुलन बनाए रखना श्रेयस्कर।'
    },
    7: {
      en: 'Brings an affectionate, beautiful, and cultured life companion. Ensures blissful romantic compatibility and lucrative artistic alliances.',
      hi: 'सुंदर, सुरुचिपूर्ण और निष्ठावान जीवनसाथी दिलाता है। दांपत्य में प्रगाढ़ प्रेम और व्यापारिक अनुबंधों में लाभ।'
    },
    8: {
      en: 'Brings financial assets through spouse or marriage alliance, magnetic intimacy, hidden wealth, and peaceful longevity.',
      hi: 'विवाह व साझेदारियों से धन लाभ, आकर्षक व्यक्तित्व, अप्रत्याशित संपत्ति और गुप्त कलाओं में रुचि।'
    },
    9: {
      en: 'Bestows fortune through arts, fashion, international culture, graceful devotions, and enjoyable journeys with loved ones.',
      hi: 'कला, सौंदर्य, दूरस्थ संस्कृतियों और सौंदर्यपरक धर्मकार्यों से भाग्योदय। सुखद व सुरुचिपूर्ण यात्राएं।'
    },
    10: {
      en: 'Grants career fame in creative arts, entertainment, luxury goods, design, public diplomacy, or lifestyle industries.',
      hi: 'कला, फैशन, सिनेमा, डिजाइन, आतिथ्य या जनसंपर्क के क्षेत्र में उच्च सफलता, कीर्ति और धनोपार्जन।'
    },
    11: {
      en: 'Generates opulent income through luxury commerce, feminine network connections, elite social circles, and fulfilled pleasures.',
      hi: 'विलासिता, कला और समाज के विशिष्ट वर्गों से प्रचुर आर्थिक लाभ। मनोकामनाओं और सुख-साधनों की भरपूर प्राप्ति।'
    },
    12: {
      en: 'Highest exaltation energy for Venus. Grants luxurious comforts, romantic peace, fine tastes, and generous expenditure on arts.',
      hi: 'शयन सुख, विलासिता, कला व सौंदर्य पर सुखद व्यय, और एकांत में भी मन की परम शांति व संतुष्टि।'
    }
  },
  Saturn: {
    1: {
      en: 'Instills mature wisdom, disciplined perseverance, serious life focus, and enduring character forged through early patience.',
      hi: 'गंभीर व्यक्तित्व, असाधारण धैर्य, अनुशासित जीवनशैली और समय के साथ सुदृढ़ होने वाली दृढ़ प्रतिष्ठा देता है।'
    },
    2: {
      en: 'Fosters conservative financial prudence, deliberate and measured speech, steady accumulation of wealth through sustained long-term effort.',
      hi: 'मितव्ययिता, नपी-तुली व गंभीर वाणी, और निरंतर परिश्रम से दीर्घकालिक अचल संपत्ति का संचय कराता है।'
    },
    3: {
      en: 'Bestows indefatigable determination, organizational mastery, practical technical skills, and unyielding courage against odds.',
      hi: 'अथक परिश्रम की शक्ति, संगठनात्मक क्षमता, व्यावहारिक कौशल और कठिनाइयों के आगे न झुकने वाला पराक्रम।'
    },
    4: {
      en: 'Anchors deep roots in ancestral heritage, enduring property holdings, and emotional resilience built through life duties.',
      hi: 'पैतृक परंपराओं से जुड़ाव, पुरानी अचल संपत्तियों का लाभ और कर्तव्यनिष्ठा से मिलने वाली मानसिक मजबूती।'
    },
    5: {
      en: 'Deepens serious research intellect, philosophical concentration, disciplined study, and thoughtful responsibility toward children.',
      hi: 'गहन शोध बुद्धि, प्राचीन विद्याओं में एकाग्रता, दार्शनिक चिंतन और संतान के प्रति कर्तव्यपरायणता।'
    },
    6: {
      en: 'Supreme placement for Saturn. Crushes long-term debts, overcomes hostile opposition through endurance, and masters complex disciplines.',
      hi: 'शनि का अत्यंत शुभ भाव। शत्रुओं, रोगों और कर्जों को पूरी तरह समाप्त करता है। कठिन से कठिन कार्य में विजय।'
    },
    7: {
      en: 'Attains Digbala. Brings a mature, loyal, and steadfast life partner. Fosters enduring business partnerships founded on integrity.',
      hi: 'दिग्बली होकर निष्ठावान, परिपक्व और कर्तव्यनिष्ठ जीवनसाथी दिलाता है। व्यापार में टिकाऊ और प्रामाणिक साझेदारी।'
    },
    8: {
      en: 'Ayush Karaka (Signifier of Longevity). Bestows long life, calm resilience during emergencies, mastery over occult sciences and patience.',
      hi: 'आयु कारक। दीर्घायु का वरदान, संकटों में अद्भुत स्थिरता, और गुप्त रहस्यों व जीवन के सत्य को समझने की क्षमता।'
    },
    9: {
      en: 'Grounds faith in classical traditions, respect for ancient scriptures, patience in destiny fruition, and enduring moral ethics.',
      hi: 'सनातन परंपराओं में गहरी निष्ठा, दार्शनिक गंभीरता और समय के साथ धीरे-धीरे परंतु स्थायी भाग्योदय।'
    },
    10: {
      en: 'Builds colossal career authority through methodical hard work, administrative grit, industrial or governmental leadership, and lasting legacy.',
      hi: 'धीमी परंतु अडिग कार्यक्षेत्रीय सफलता, संगठनात्मक नेतृत्व, उद्योगों/प्रशासन में शीर्ष अधिकार और स्थायी कीर्ति।'
    },
    11: {
      en: 'Generates massive long-term financial wealth, reliable elder mentors, and ultimate fulfillment of enduring life ambitions.',
      hi: 'शनि का परम लाभ भाव। निरंतर आर्थिक आमदनी, वृद्धजनों का सहयोग और जीवन के सभी बड़े लक्ष्यों की ठोस पूर्ति।'
    },
    12: {
      en: 'Encourages meditative discipline, detachment from ephemeral materialism, peace in seclusion, and selfless service.',
      hi: 'त्याग, वैराग्य, एकांतवास में आत्मशांति, परोपकारी कर्म और मोक्ष मार्ग की ओर स्वाभाविक झुकाव प्रदान करता है।'
    }
  },
  Rahu: {
    1: {
      en: 'Bestows magnetic aura, intense ambition to break conventional molds, innovative mindset, and bold forward-looking presence.',
      hi: 'अपरंपरागत व सम्मोहक व्यक्तित्व, दुनिया में कुछ बड़ा करने की तीव्र महत्वाकांक्षा और नए विचारों की ओर आकर्षण।'
    },
    2: {
      en: 'Drives sharp commercial wit, unconventional wealth generation, influential persuasive speech, and appetite for rare resources.',
      hi: 'व्यापारिक चातुर्य, आधुनिक या विदेशी माध्यमों से त्वरित धनोपार्जन और प्रभावशाली व सम्मोहक वाणी।'
    },
    3: {
      en: 'Supreme placement for Rahu. Imparts audacious courage, pioneer in digital technologies, media triumphs, and fearless enterprise.',
      hi: 'राहु का अत्यंत शुभ भाव। निडर पराक्रम, आधुनिक मीडिया व तकनीक में महारत और साहसिक कार्यों में बड़ी सफलताएं।'
    },
    4: {
      en: 'Fosters affinity for modern urban properties, foreign homeland connections, and innovative conveniences; invites grounding practices.',
      hi: 'आधुनिक सुख-सुविधाएं, विदेश या दूरस्थ स्थानों में निवास और नए जमाने के भवनों/वाहनों का सुख।'
    },
    5: {
      en: 'Sparks unconventional genius, speculative foresight, technological creativity, and affinity for cutting-edge intellectual breakthroughs.',
      hi: 'आउट-ऑफ-द-बॉक्स सोच, आधुनिक तकनीक व अनुसंधान में प्रतिभा और अप्रत्याशित बौद्धिक उपलब्धियां।'
    },
    6: {
      en: 'Formidable placement for Rahu. Demolishes competitors using innovative tactics, ensures competitive exam triumphs, and defies health setbacks.',
      hi: 'राहु का श्रेष्ठ भाव। शत्रुओं पर कूटनीतिक विजय, प्रतियोगी परीक्षाओं में अप्रत्याशित सफलता और बाधाओं का निवारण।'
    },
    7: {
      en: 'Attracts an unconventional partner often from a different cultural background; unlocks foreign alliances and expansive public commerce.',
      hi: 'अलग पृष्ठभूमि या संस्कृति का जीवनसाथी, विदेशी साझेदारियां और आधुनिक व्यापारिक अनुबंधों में उन्नति।'
    },
    8: {
      en: 'Drives fascination with occult mysteries, sudden unexpected financial inheritances, and penetrating psychological investigations.',
      hi: 'गूढ़ रहस्यों, अचानक धन लाभ, बीमा/विरासत के मामलों और अप्रत्याशित जीवन परिवर्तनों से लाभ।'
    },
    9: {
      en: 'Inspires unorthodox spiritual perspectives, extensive global voyages, breaking dogmatic boundaries, and cross-cultural philosophy.',
      hi: 'रूढ़िवादिता से परे दार्शनिक सोच, दूरस्थ देशों की यात्राएं और वैश्विक ज्ञान के समन्वय से भाग्योदय।'
    },
    10: {
      en: 'Catapults to sudden political, corporate, or media prominence; grants public charisma, modern executive power, and mass outreach.',
      hi: 'राजनीति, कॉरपोरेट, मीडिया या तकनीक के क्षेत्र में अप्रत्याशित ऊंचाई, भारी जनसमर्थन और त्वरित प्रसिद्धि।'
    },
    11: {
      en: 'Supreme windfall placement for Rahu. Unlocks massive financial gains, influential global networks, and explosive fulfillment of worldly desires.',
      hi: 'राहु का सबसे लाभकारी भाव। अप्रत्याशित धन लाभ, अंतरराष्ट्रीय संपर्क और बड़े-बड़े भौतिक सपनों की त्वरित पूर्ति।'
    },
    12: {
      en: 'Promotes foreign residency, innovative international ventures, deep subconscious awareness, and metaphysical curiosity.',
      hi: 'विदेश प्रवास, अंतरराष्ट्रीय व्यापार, आध्यात्मिक रहस्यवाद और गुप्त विद्याओं में गहरी रुचि।'
    }
  },
  Ketu: {
    1: {
      en: 'Imparts spiritual intuition, detached contemplative demeanor, mystical insight, and deep introspective perception.',
      hi: 'आध्यात्मिक अंतर्ज्ञान, वैराग्यपूर्ण स्वभाव, रहस्यमयी आकर्षण और जीवन के सूक्ष्म सत्यों को सहज समझ लेना।'
    },
    2: {
      en: 'Fosters detachment from material accumulation, truthful and cryptic speech, intuitive financial insights, and spiritual lineage roots.',
      hi: 'धन के प्रति अनासक्ति, खरी व रहस्यमयी वाणी, और पैतृक आध्यात्मिक संस्कारों से जुड़ाव।'
    },
    3: {
      en: 'Bestows intuitive courage, silent inner strength, subtle communication capabilities, and self-directed independence.',
      hi: 'आंतरिक आत्मबल, शांत पराक्रम, सूक्ष्म संवाद कौशल और स्वतंत्र रूप से कार्य करने की क्षमता।'
    },
    4: {
      en: 'Nurtures inner emotional detachment, yearning for peaceful retreat sanctuaries, simple living, and meditative domestic stillness.',
      hi: 'भौतिक सुखों के प्रति वैराग्य, एकांत व शांत वातावरण की चाह, और आंतरिक आत्मशांति में संतुष्टि।'
    },
    5: {
      en: 'Bestows past-life spiritual merits (Purva Punya), profound intuitive intelligence, interest in mantras, and unconventional creativity.',
      hi: 'पूर्वजन्म के पुण्य, मंत्र-साधना में रुचि, तीक्ष्ण अंतःप्रेरणा और आध्यात्मिक विषयों में स्वाभाविक मेधा।'
    },
    6: {
      en: 'Dissolves obstacles and disputes through spiritual indifference, natural immunity against ailments, and resilience in adversity.',
      hi: 'शत्रुओं व बाधाओं की उपेक्षा कर उन पर विजय पाना, असाधारण मानसिक दृढ़ता और रोग-प्रतिरोधक क्षमता।'
    },
    7: {
      en: 'Brings an introverted or spiritually inclined partner; deepens relationship through spiritual affinity rather than shallow glamour.',
      hi: 'आध्यात्मिक, शांत या अंतर्मुखी जीवनसाथी; संबंध में सांसारिक दिखावे से अधिक आत्मिक जुड़ाव की आवश्यकता।'
    },
    8: {
      en: 'Supreme placement for Ketu. Unlocks deep occult mastery, psychic sensitivity, profound spiritual rebirth, and transformative breakthroughs.',
      hi: 'केतु का अत्यंत शक्तिशाली भाव। तंत्र, ज्योतिष, गूढ़ विद्याओं और आध्यात्मिक पुनर्जन्म में अद्वितीय सिद्धि।'
    },
    9: {
      en: 'Fosters pure spiritual devotion, longing for sacred shrines and pilgrimages, detachment from empty dogma, and quest for absolute Truth.',
      hi: 'सच्ची ईश्वरीय भक्ति, तीर्थाटन, गुरु चरणों में अनुराग और रूढ़ियों से मुक्त होकर आत्मज्ञान की खोज।'
    },
    10: {
      en: 'Confers humble worldly pride, excellence in advisory, healthcare, research, or spiritual guidance behind the scenes.',
      hi: 'कार्यक्षेत्र में बिना दिखावे के गहरी विशेषज्ञता, परामर्श, अनुसंधान या सेवाभावी कार्यों में उच्च सम्मान।'
    },
    11: {
      en: 'Attracts unexpected or periodic gains, spiritually refined friendships, and indifference to superficial material competition.',
      hi: 'अप्रत्याशित धन लाभ, सात्विक व बौद्धिक मित्रों का साथ, और दिखावटी होड़ से परे रहकर संतोष।'
    },
    12: {
      en: 'Supreme Moksha Karaka (Liberation). Bestows high meditative absorption, spiritual transcendence, divine grace, and freedom from worldly bonds.',
      hi: 'मोक्ष का परम भाव। समाधि, ध्यान-साधना में परम आनंद, सांसारिक बंधनों से मुक्ति और ईश्वरीय साक्षात्कार।'
    }
  }
};

const getPlanetHouseDetailedResult = (
  planetName: string,
  targetHouse: number,
  isLord: boolean,
  isOccupant: boolean,
  isAspecting: boolean,
  dignityLabel: string,
  funcNature: string,
  isHi: boolean
): string => {
  const planetData = PLANET_HOUSE_RESULTS[planetName]?.[targetHouse];
  const baseResult = planetData 
    ? (isHi ? planetData.hi : planetData.en) 
    : (isHi ? `${planetName} इस भाव में अपने स्वाभाविक गुण-धर्मों के अनुसार फल प्रदान करता है।` : `${planetName} channels its natural significations into this house.`);
  
  const roleContext = isLord
    ? (isHi 
        ? ` [भावेश: इस भाव का अधिपति होने के कारण यह इसके आधार, स्थायित्व और अंतिम फल-सिद्धि का मुख्य संचालक है।]` 
        : ` [House Lord: Governing this bhava, it determines the bedrock foundation and fruition of this house's significations.]`)
    : isOccupant
    ? (isHi 
        ? ` [भाव स्थित: इस भाव में सीधे विराजमान होकर यह अपनी संपूर्ण ऊर्जा को इसके मामलों में प्रत्यक्ष संचारित करता है।]` 
        : ` [Occupant: Directly seated in this house, projecting its planetary essence directly onto house matters.]`)
    : (isHi 
        ? ` [दृष्टिकर्ता: इस भाव पर दृष्टि डालकर दूर से अपनी ऊर्जा और प्रभाव द्वारा इसके फलों को सक्रिय करता है।]` 
        : ` [Aspecting: Casting its vision upon this house, modifying and energizing its outcomes from across the chart.]`);

  return `${baseResult}${roleContext}`;
};

export interface HolisticNetHouseSynthesis {
  netScore: number;
  verdictTitle: string;
  verdictColor: 'emerald' | 'teal' | 'amber' | 'rose';
  netVectorSummary: string;
  drivers: string[];
  frictions: string[];
  lifeOutcomeVerdict: string;
  dashaNote?: string;
}

export const computeHolisticHouseNetSynthesis = (
  houseNumber: number,
  sign: string,
  signLord: string,
  signLordPosition: any,
  sarvashtakvargaPoints: number | null,
  occupants: any[],
  aspects: any[],
  shadbalaPlanets: any[] | undefined,
  currentMD: any,
  currentAD: any,
  isHi: boolean,
  ascSign?: string,
  parivartana?: any
): HolisticNetHouseSynthesis => {
  let netScore = 0;
  const drivers: string[] = [];
  const frictions: string[] = [];

  // 1. House Lord analysis
  if (signLordPosition) {
    const lordHouse = signLordPosition.house;
    const isKendra = [1, 4, 7, 10].includes(lordHouse);
    const isTrikona = [5, 9].includes(lordHouse);
    const isDusthana = [6, 8, 12].includes(lordHouse);
    const isUpachaya = [3, 6, 10, 11].includes(lordHouse);

    if (isTrikona || isKendra) {
      netScore += 2;
      drivers.push(
        isHi
          ? `भावेश ${signLord} शुभ ${isTrikona ? 'त्रिकोण' : 'केंद्र'} (भाव ${lordHouse}) में स्थित होकर इस भाव को सुदृढ़ आधार प्रदान कर रहे हैं।`
          : `Lord ${signLord} is fortified in an auspicious ${isTrikona ? 'Trikona' : 'Kendra'} (House ${lordHouse}), providing robust foundational backing.`
      );
    } else if (isDusthana && lordHouse !== houseNumber) {
      netScore -= 2;
      frictions.push(
        isHi
          ? `भावेश ${signLord} दुस्थान (भाव ${lordHouse}) में स्थित हैं, जिससे ऊर्जा में बिखराव, अवरोध या विलंब की संभावना रहती है।`
          : `Lord ${signLord} sits in Dusthana (House ${lordHouse}), causing energy dissipation, friction, or timing delays.`
      );
    } else if (isUpachaya && !isDusthana) {
      netScore += 1;
      drivers.push(
        isHi
          ? `भावेश ${signLord} उपचय भाव (${lordHouse}) में स्थित हैं, जिससे समय के साथ निरंतर प्रगति एवं दृढ़ता प्राप्त होती है।`
          : `Lord ${signLord} sits in an Upachaya house (${lordHouse}), indicating steady compounding growth and improvement over time.`
      );
    }

    // Dignity
    const dig = signLordPosition.dignity;
    if (dig === 'Exalted' || dig === 'Own House') {
      netScore += 2.5;
      drivers.push(
        isHi
          ? `भावेश ${signLord} ${dig === 'Exalted' ? 'उच्च' : 'स्वराशि'} स्थिति में हैं, जो भाव को उच्च कोटि का सामर्थ्य व गरिमा प्रदान करता है।`
          : `Lord ${signLord} holds ${dig} dignity, commanding peak authoritative potency and self-sufficiency.`
      );
    } else if (dig === 'Debilitated') {
      netScore -= 2.5;
      frictions.push(
        isHi
          ? `भावेश ${signLord} नीच राशि में हैं, जिससे भाव के नैसर्गिक फलों को अर्जित करने हेतु विशेष संघर्ष व संयम अपेक्षित है।`
          : `Lord ${signLord} is in debilitation (Neecha), creating vulnerability that requires conscious remedial fortification.`
      );
    }

    // Lord Shadbala
    const lordShad = shadbalaPlanets?.find(s => s.planet === signLord);
    if (lordShad) {
      if (lordShad.ratio >= 1.15) {
        netScore += 1.5;
        drivers.push(
          isHi
            ? `भावेश ${signLord} का षड्बल असाधारण (${lordShad.ratio.toFixed(2)}x) है, जो बाधाओं से उबरने की मजबूत सहनशक्ति देता है।`
            : `Lord ${signLord} commands exceptional Shadbala (${lordShad.ratio.toFixed(2)}x Virupas), providing great resilience against afflictions.`
        );
      } else if (lordShad.ratio < 0.9) {
        netScore -= 1;
        frictions.push(
          isHi
            ? `भावेश ${signLord} का षड्बल औसत से न्यून (${lordShad.ratio.toFixed(2)}x) है, जिससे यह भाव प्रतिकूल गोचर के प्रति अधिक संवेदनशील रहता है।`
            : `Lord ${signLord} has below-average Shadbala (${lordShad.ratio.toFixed(2)}x), making the house more sensitive to transit pressures.`
        );
      }
    }
  }

  // Parivartana Yoga
  if (parivartana?.isAuspicious) {
    netScore += 2.5;
    drivers.push(
      isHi
        ? `शुभ परिवर्तन योग (Parivartana Yoga): भावेश ${signLord} एवं अन्य भाव के मध्य परस्पर राशि परिवर्तन इस भाव को राजयोग तुल्य बल प्रदान करता है।`
        : `Auspicious Parivartana Yoga: Mutual sign exchange involving Lord ${signLord} empowers this house with high raja-yoga potency.`
    );
  }

  // 2. Occupants Evaluation (Functional Nature, Yogakaraka, Upachaya & Dignity)
  occupants.forEach(occ => {
    const pName = occ.name;
    const func = ascSign ? getFunctionalNature(ascSign as any, pName as any) : null;
    const isYogakaraka = occ.isYogakaraka || func?.nature === 'Yogakaraka';
    const isFuncBenefic = occ.isFunctionalBenefic || func?.nature === 'Functional Benefic';
    const isNatBenefic = ['Jupiter', 'Venus', 'Moon', 'Mercury'].includes(pName);
    const isUpachayaHouse = [3, 6, 10, 11].includes(houseNumber);

    if (isYogakaraka) {
      netScore += 2.5;
      drivers.push(
        isHi
          ? `परम योगकारक ग्रह ${pName} भाव ${houseNumber} में स्थित होकर सर्वोच्च राजयोग, पराक्रम व शुभत्व का संचार कर रहे हैं।`
          : `Supreme Yogakaraka ${pName} occupies House ${houseNumber}, infusing top-tier executive power, auspicious drive, and protective strength.`
      );
    } else if (isFuncBenefic) {
      netScore += 1.5;
      drivers.push(
        isHi
          ? `कार्यात्मक शुभ ग्रह ${pName} भाव ${houseNumber} में उपस्थित रहकर सकारात्मक ऊर्जा व रचनात्मक संबल प्रदान कर रहे हैं।`
          : `Functional Benefic ${pName} resides in House ${houseNumber}, providing constructive development and protective backing.`
      );
    } else if (isUpachayaHouse && !isNatBenefic) {
      netScore += 1.2;
      drivers.push(
        isHi
          ? `उपचय भाव (${houseNumber}) में ${pName} की उपस्थिति 'शत्रु-हंता' व पराक्रम योग निर्मित करती है, जो संघर्षों को सफलता में बदलती है।`
          : `Natural malefic ${pName} in Upachaya House ${houseNumber} activates 'Shatru-Hanta' (obstacle-conquering) potency, transforming friction into ambition and victory.`
      );
    } else if (isNatBenefic) {
      netScore += 1.5;
      drivers.push(
        isHi
          ? `नैसर्गिक शुभ ग्रह ${pName} भाव ${houseNumber} में उपस्थित रहकर सौम्यता, सुख व समृद्धि का संचार कर रहे हैं।`
          : `Natural Benefic ${pName} resides inside House ${houseNumber}, providing graceful protection and auspicious blessings.`
      );
    } else {
      netScore -= 1.2;
      frictions.push(
        isHi
          ? `क्रूर/प्रतिकूल ग्रह ${pName} की उपस्थिति भाव में दबाव, उग्रता या कड़े आत्म-अनुशासन की मांग करती है।`
          : `Challenging occupant ${pName} introduces pressure or karmic tests, demanding structured discipline in house affairs.`
      );
    }

    // Occupant Dignity & Combustion modifiers
    if (occ.dignity === 'Exalted') {
      netScore += 1.5;
      drivers.push(
        isHi
          ? `स्थित ग्रह ${pName} उच्च राशि में होकर भाव को अतिरिक्त पराक्रम व प्रतिष्ठा प्रदान कर रहे हैं।`
          : `Occupant ${pName} is exalted in this sign, elevating authoritative power and reputation.`
      );
    } else if (occ.dignity === 'Own House') {
      netScore += 1.2;
      drivers.push(
        isHi
          ? `स्थित ग्रह ${pName} स्वराशि में स्थित होकर भाव को आत्मनिर्भर सुरक्षा प्रदान कर रहे हैं।`
          : `Occupant ${pName} is in its own sign, reinforcing foundational security.`
      );
    } else if (occ.dignity === 'Debilitated') {
      netScore -= 1.5;
      frictions.push(
        isHi
          ? `स्थित ग्रह ${pName} नीच राशि में होने के कारण इसके नैसर्गिक व कार्यात्मक कारकत्वों हेतु अतिरिक्त प्रयास अपेक्षित हैं।`
          : `Occupant ${pName} is in debilitation (Neecha), necessitating conscious remedial reinforcement.`
      );
    }
    if (occ.isCombust) {
      netScore -= 1.0;
      frictions.push(
        isHi
          ? `स्थित ग्रह ${pName} अस्त (Combust) होने से इसकी सक्रिय अभिव्यक्ति में बाधा संभव है।`
          : `Occupant ${pName} is combust (Asta), muting its direct outward manifestation.`
      );
    }
  });

  // 3. Aspects (Drishti)
  aspects.forEach(asp => {
    const pName = asp.planet;
    const func = ascSign ? getFunctionalNature(ascSign as any, pName as any) : null;
    const isYogakaraka = func?.nature === 'Yogakaraka';
    const isFuncBenefic = func?.nature === 'Functional Benefic';
    const isNatBenefic = ['Jupiter', 'Venus', 'Moon', 'Mercury'].includes(pName);

    if (isYogakaraka) {
      netScore += 1.5;
      drivers.push(
        isHi
          ? `परम योगकारक ग्रह ${pName} की शुभ दृष्टि भाव ${houseNumber} को विशेष सुरक्षा कवच एवं उन्नतिकारक प्रभाव प्रदान कर रही है।`
          : `Auspicious aspect from Supreme Yogakaraka ${pName} casts a protective and elevating aura over House ${houseNumber}.`
      );
    } else if (isFuncBenefic) {
      netScore += 1.2;
      drivers.push(
        isHi
          ? `कार्यात्मक शुभ ग्रह ${pName} की दृष्टि भाव के फलों को पोषण एवं उन्नति प्रदान करती है।`
          : `Supportive aspect from Functional Benefic ${pName} nurtures and elevates house affairs.`
      );
    } else if (asp.isBenefic || isNatBenefic) {
      netScore += 1.0;
      drivers.push(
        isHi
          ? `${pName} की सौम्य दृष्टि भाव ${houseNumber} को सुरक्षा कवच एवं शुभ ऊर्जा प्रदान कर रही है।`
          : `Auspicious aspect from ${pName} provides graceful protection and beneficial nourishment.`
      );
    } else {
      netScore -= 1.0;
      frictions.push(
        isHi
          ? `${pName} की क्रूर दृष्टि भाव में तनाव, विलंब अथवा धैर्य की परीक्षा उत्पन्न कर सकती है।`
          : `Challenging aspect from ${pName} creates friction, testing patience and resilience in house affairs.`
      );
    }
  });

  // 4. Ashtakavarga SAV Points
  const sav = sarvashtakvargaPoints ?? 28;
  if (sav >= 32) {
    netScore += 2.0;
    drivers.push(
      isHi
        ? `असाधारण अष्टकवर्ग आधार (${sav}/56 SAV अंक) प्रतिकूल गोचर को सहने और शुभ चक्रों को शीघ्र फलीभूत करने में अत्यंत समर्थ है।`
        : `Stout Ashtakavarga soil (${sav}/56 SAV pts) acts as an elastic cushion, absorbing transit shocks and magnifying benefic cycles.`
    );
  } else if (sav >= 28) {
    netScore += 1.0;
    drivers.push(
      isHi
        ? `अनुकूल अष्टकवर्ग आधार (${sav}/56 SAV अंक) इस भाव के कार्यों के लिए स्थिर व उर्वर भूमि प्रदान करता है।`
        : `Fertile Ashtakavarga soil (${sav}/56 SAV pts) stands comfortably above the 28 baseline, providing steady ground.`
    );
  } else if (sav < 25) {
    netScore -= 1.5;
    frictions.push(
      isHi
        ? `अल्प अष्टकवर्ग अंक (${sav}/56 SAV अंक) के कारण यह भाव गोचर के उतार-चढ़ाव के प्रति संवेदनशील रहता है।`
        : `Deficient Ashtakavarga points (${sav}/56 SAV pts) leave the house soil porous and vulnerable to adverse transits.`
    );
  }

  // 5. Dasha Activation
  let dashaNote: string | undefined;
  const activePlanets = [currentMD?.planet, currentAD?.planet].filter(Boolean);
  const houseActiveGraha = activePlanets.find(p => p === signLord || occupants.some(o => o.name === p) || aspects.some(a => a.planet === p));
  if (houseActiveGraha) {
    dashaNote = isHi
      ? `⚡ तात्कालिक दशा संरेखण: वर्तमान में चल रही ${houseActiveGraha} की दशा इस भाव को सीधे सक्रिय कर रही है, अतः इसके परिणाम वर्तमान जीवन काल में प्रमुखता से दृष्टिगोचर होंगे।`
      : `⚡ Active Timeline Activation: The ongoing Dasha of ${houseActiveGraha} directly governs or influences this house, bringing its outcomes into sharp focus during this current chapter of life.`;
  }

  // Verdict Synthesis
  let verdictTitle = '';
  let verdictColor: 'emerald' | 'teal' | 'amber' | 'rose' = 'teal';
  let netVectorSummary = '';

  if (netScore >= 3.5) {
    verdictColor = 'emerald';
    verdictTitle = isHi ? 'अत्यंत शुभ एवं प्रभावशाली' : 'Highly Fortified & Auspicious';
    netVectorSummary = isHi
      ? `भाव ${houseNumber} की समग्र ऊर्जा अत्यधिक सशक्त व संतुलित है। भावेश की सुदृढ़ स्थिति, शुभ ग्रहों का सहयोग एवं सुदृढ़ अष्टकवर्ग संबल मिलकर इस भाव के मामलों में निरंतर सफलता, स्थायित्व और शुभ परिणाम सुनिश्चित करते हैं।`
      : `House ${houseNumber} commands exceptional astrological vitality. The harmonious convergence of lord dignity, auspicious planetary activation, and robust Ashtakavarga support ensures that the significations of this house flourish with minimal friction and consistent fruition.`;
  } else if (netScore >= 1.0) {
    verdictColor = 'teal';
    verdictTitle = isHi ? 'सशक्त एवं अनुकूल' : 'Strong & Favorable';
    netVectorSummary = isHi
      ? `भाव ${houseNumber} में सकारात्मक ऊर्जाओं का बाहुल्य है। यद्यपि कुछ सूक्ष्म चुनौतियां या गोचरीय दबाव उपस्थित हो सकते हैं, परंतु भावेश का संबल एवं अष्टकवर्ग आधार इसे समय के साथ उत्तरोत्तर उन्नति और संतोषजनक फल प्रदान करने में सक्षम बनाते हैं।`
      : `House ${houseNumber} maintains a predominantly positive momentum. While minor tests or transit friction may periodically arise, the structural strength of the lord and soil enables steady compounding gains and favorable real-world results.`;
  } else if (netScore >= -1.0) {
    verdictColor = 'amber';
    verdictTitle = isHi ? 'संतुलित एवं स्थिर (परिश्रम से सिद्धि)' : 'Balanced & Effort-Driven';
    netVectorSummary = isHi
      ? `भाव ${houseNumber} संतुलित एवं मिश्रित फल प्रदान करता है। यहां शुभ एवं क्रूर प्रभावों का द्वंद्व है। सफलता स्वतः नहीं मिलेगी, अपितु जातक के निरंतर व्यक्तिगत प्रयास, संयम और अनुशासन से ही इस भाव के पूर्ण फल फलीभूत होंगे।`
      : `House ${houseNumber} displays a balanced, dual-natured energetic field. Both supportive drivers and friction points coexist here. Outcomes will not materialize automatically, but will reward disciplined persistence, strategic planning, and patience.`;
  } else {
    verdictColor = 'rose';
    verdictTitle = isHi ? 'संवेदनशील — सुरक्षात्मक प्रबंधन अपेक्षित' : 'Challenging — Vigilance Required';
    netVectorSummary = isHi
      ? `भाव ${houseNumber} पर इस समय दबाव व संवेदनशीलता अधिक है। भावेश की निर्बलता, क्रूर ग्रहों की दृष्टि/उपस्थिति या अल्प अष्टकवर्ग बल के कारण तात्कालिक बाधाएं आ सकती हैं। इसके लिए सुरक्षात्मक दृष्टिकोण, धैर्य और विशेष कर्म सुधार (उपाय) आवश्यक हैं।`
      : `House ${houseNumber} requires vigilant stewardship. The convergence of challenging aspects, lord vulnerabilities, or lighter Ashtakavarga support suggests that matters governed by this house are susceptible to stress, requiring conscious karmic mitigation and emotional resilience.`;
  }

  // Real-world manifestation per house
  const houseManifestations: Record<number, { en: string; hi: string }> = {
    1: {
      en: 'Directly impacts physical vitality, self-confidence, aura, and personal leadership trajectory.',
      hi: 'शारीरिक आरोग्य, आत्मबल, व्यक्तिगत गरिमा, व्यक्तित्व की आभा और जीवन के समग्र दिशा-निर्देश को तय करता है।'
    },
    2: {
      en: 'Determines financial liquid savings, family wealth preservation, speech credibility, and daily nourishment.',
      hi: 'संचित धन, पारिवारिक सामंजस्य, वाणी की सत्यता व प्रभावशीलता, और आर्थिक सुरक्षा को संचालित करता है।'
    },
    3: {
      en: 'Governs initiative, entrepreneurial courage, communication mastery, and rapport with siblings/associates.',
      hi: 'उद्यमी साहस, नई पहल करने की शक्ति, प्रभावी संवाद, और बंधु-बांधवों के साथ मधुर संबंधों को निर्देशित करता है।'
    },
    4: {
      en: 'Dictates inner peace (Sukha), emotional stability, property/real estate acquisition, and maternal harmony.',
      hi: 'आंतरिक मानसिक शांति, गृह-सुख, माता का सान्निध्य, और स्थायी संपत्ति (भूमि/भवन/वाहन) के सुख को प्रभावित करता है।'
    },
    5: {
      en: 'Directs intellectual discrimination, creative enterprise, speculative foresight, and lineage/children success.',
      hi: 'बुद्धिमत्ता, पूर्वजन्म के पुण्यों का फलोदय, रचनात्मक प्रतिभा, संतान सुख और रणनीतिक निर्णय क्षमता को दर्शाता है।'
    },
    6: {
      en: 'Controls ability to overcome competitors, manage debts, resolve disputes, and maintain daily vitality/immunity.',
      hi: 'प्रतिद्वंद्वियों पर विजय, ऋण-मुक्ति, विवादों का शांतिपूर्ण समाधान, और शारीरिक रोग-प्रतिरोधक क्षमता को नियंत्रित करता है।'
    },
    7: {
      en: 'Shapes marital companionship, long-term business partnerships, public reputation, and contractual synergy.',
      hi: 'वैवाहिक जीवन का सुख, व्यापारिक साझेदारियों में संतुलन, जनसंपर्क, और बाहरी दुनिया के साथ सौहार्दपूर्ण समझौतों को तय करता है।'
    },
    8: {
      en: 'Governs crisis resilience, sudden transformations, longevity, research acumen, and unearned inheritance/wealth.',
      hi: 'संकटों से उबरने का आत्मबल, अचानक होने वाले जीवन परिवर्तन, दीर्घायु, और गूढ़ अनुसंधानिक क्षमता को आकार देता है।'
    },
    9: {
      en: 'Channels fortune (Bhagya), ethical wisdom, higher education, guidance of mentors/father, and spiritual pilgrimages.',
      hi: 'भाग्य का संबल, धर्म-परायणता, गुरु व पिता का आशीर्वाद, उच्च ज्ञान और आध्यात्मिक यात्राओं की सफलता को निर्धारित करता है।'
    },
    10: {
      en: 'Establishes professional status, career authority, public influence, and accomplishment of life missions (Karma).',
      hi: 'कार्यक्षेत्र में अधिकार, सामाजिक प्रतिष्ठा, आजीविका में स्थिरता, और जीवन के महान उद्देश्यों की पूर्ति को संचालित करता है।'
    },
    11: {
      en: 'Magnifies recurring revenues, fulfillment of long-term desires, elder sibling bonds, and influential networking.',
      hi: 'आय के निरंतर स्रोत, महत्वाकांक्षाओं की पूर्ति, वरिष्ठ मित्रों का सहयोग, और सामाजिक प्रभाव क्षेत्र के विस्तार को दर्शाता है।'
    },
    12: {
      en: 'Influences restful sleep, subconscious equanimity, foreign opportunities, and detachment leading to spiritual Moksha.',
      hi: 'गहरी मानसिक शांति, विदेश यात्रा/संबंध, व्यय पर नियंत्रण, और आत्मिक मुक्ति व ध्यान-साधना के मार्ग को प्रशस्त करता है।'
    }
  };

  const lifeOutcomeVerdict = houseManifestations[houseNumber]
    ? (isHi ? houseManifestations[houseNumber].hi : houseManifestations[houseNumber].en)
    : (isHi ? 'भाव के फल जातक के कर्म एवं समय अनुसार प्रकट होते हैं।' : 'Outcomes manifest according to the native\'s karmic action and planetary timing.');

  return {
    netScore,
    verdictTitle,
    verdictColor,
    netVectorSummary,
    drivers,
    frictions,
    lifeOutcomeVerdict,
    dashaNote
  };
};

export const computeLaymanPracticalSummary = (
  houseNumber: number,
  sign: string,
  signLord: string,
  occupants: any[],
  aspects: any[],
  shadbalaPlanets: any[] | undefined | null,
  dashaActive: boolean,
  currentMD: any,
  currentAD: any,
  sarvashtakvargaPoints: number | null,
  ascSign: string,
  isHi: boolean
): HolisticNetHouseSynthesis => {
  return computeHolisticHouseNetSynthesis(
    houseNumber,
    sign,
    signLord,
    null,
    sarvashtakvargaPoints,
    occupants,
    aspects,
    shadbalaPlanets ?? undefined,
    currentMD,
    currentAD,
    isHi,
    ascSign
  );
};

export interface HouseSynthesizedUpaya {
  quickSummary: string;
  behavioral: string;
  spiritual: string;
  caution: string;
  targetPlanet: string;
  mantra: string;
  chantingGuidance: string;
  daan?: string;
  dashaSynergyNote?: string;
  gemstoneCaution?: string;
}

export const computeSynthesizedUpaya = (
  houseNumber: number,
  sign: string,
  signLord: string,
  occupants: any[],
  aspects: any[],
  netScore: number,
  currentMDPlanet: string | undefined,
  currentADPlanet: string | undefined,
  sarvashtakvargaPoints: number | null,
  isHi: boolean
): HouseSynthesizedUpaya => {
  const dashaActive = currentMDPlanet === signLord || currentADPlanet === signLord;

  const mantrasByPlanet: Record<string, { mantra: string; guidanceEn: string; guidanceHi: string; daanEn: string; daanHi: string }> = {
    Sun: {
      mantra: 'ॐ ह्रां ह्रीं ह्रौं सः सूर्याय नमः',
      guidanceEn: 'Chant 108 times at sunrise facing East using a Rudraksha or red sandalwood mala.',
      guidanceHi: 'प्रातः सूर्योदय के समय पूर्व दिशा की ओर मुख करके रुद्राक्ष या लाल चंदन की माला से 108 बार जप करें।',
      daanEn: 'Donate wheat, copper utensils, or jaggery to elders or temples on Sundays.',
      daanHi: 'रविवार को गेहूं, तांबे के बर्तन, या गुड़ का दान वृद्धों अथवा मंदिर में करें।'
    },
    Moon: {
      mantra: 'ॐ श्रां श्रीं श्रौं सः चन्द्रमसे नमः',
      guidanceEn: 'Chant 108 times in the evening facing North-West using a pearl or sphatik (quartz) mala.',
      guidanceHi: 'सायंकाल उत्तर-पश्चिम दिशा की ओर मुख करके स्फटिक या मोती की माला से 108 बार जप करें।',
      daanEn: 'Donate milk, white rice, silver, or drinking water to needy mothers on Mondays.',
      daanHi: 'सोमवार को दूध, चावल, चांदी, या शीतल जल का दान जरूरतमंद माताओं अथवा कन्याओं को करें।'
    },
    Mars: {
      mantra: 'ॐ क्रां क्रीं क्रौं सः भौमाय नमः',
      guidanceEn: 'Chant 108 times on Tuesdays facing South with a red coral or red sandalwood mala.',
      guidanceHi: 'मंगलवार को दक्षिण दिशा की ओर मुख करके लाल मूंगे या रक्तचंदन की माला से 108 बार जप करें।',
      daanEn: 'Donate red lentils (masoor dal), jaggery, or blood donation on Tuesdays.',
      daanHi: 'मंगलवार को लाल मसूर की दाल, गुड़, अथवा रक्तदान करें।'
    },
    Mercury: {
      mantra: 'ॐ ब्रां ब्रीं ब्रौं सः बुधाय नमः',
      guidanceEn: 'Chant 108 times on Wednesdays facing North using a Tulsi mala.',
      guidanceHi: 'बुधवार को उत्तर दिशा की ओर मुख करके तुलसी की माला से 108 बार जप करें।',
      daanEn: 'Donate green moong dal, green clothes, or study stationery to students on Wednesdays.',
      daanHi: 'बुधवार को हरी मूंग दाल, हरे वस्त्र, अथवा विद्यार्थियों को पुस्तकें/लेखन सामग्री दान करें।'
    },
    Jupiter: {
      mantra: 'ॐ ग्रां ग्रीं ग्रौं सः गुरवे नमः',
      guidanceEn: 'Chant 108 times on Thursdays facing North-East using a turmeric or yellow sandalwood mala.',
      guidanceHi: 'गुरुवार को ईशान (उत्तर-पूर्व) दिशा की ओर मुख करके हल्दी या पीले चंदन की माला से 108 बार जप करें।',
      daanEn: 'Donate chana dal, turmeric, yellow sweets, or religious scriptures on Thursdays.',
      daanHi: 'गुरुवार को चने की दाल, हल्दी, पीले फल/मिष्ठान अथवा धार्मिक पुस्तकें दान करें।'
    },
    Venus: {
      mantra: 'ॐ द्रां द्रीं द्रौं सः शुक्राय नमः',
      guidanceEn: 'Chant 108 times on Fridays facing South-East using a white sphatik mala.',
      guidanceHi: 'शुक्रवार को आग्नेय (दक्षिण-पूर्व) दिशा की ओर मुख करके सफेद स्फटिक की माला से 108 बार जप करें।',
      daanEn: 'Donate white sugar, pure ghee, silk clothes, or curd to married women or cow shelters.',
      daanHi: 'शुक्रवार को शुद्ध घी, मिश्री, दही, अथवा रेशमी वस्त्रों का दान करें।'
    },
    Saturn: {
      mantra: 'ॐ प्रां प्रीं प्रौं सः शनैश्चराय नमः',
      guidanceEn: 'Chant 108 times on Saturdays after sunset facing West using a Rudraksha or blue sapphire/iron mala.',
      guidanceHi: 'शनिवार को सूर्यास्त के बाद पश्चिम दिशा की ओर मुख करके रुद्राक्ष की माला से 108 बार जप करें।',
      daanEn: 'Donate black sesame seeds, mustard oil, dark blankets, or service to laborers and handicapped.',
      daanHi: 'शनिवार को काले तिल, सरसों का तेल, काला कंबल, अथवा दिव्यांगों व सफाईकर्मियों की सेवा/सहायता करें।'
    },
    Rahu: {
      mantra: 'ॐ भ्रां भ्रीं भ्रौं सः राहवे नमः',
      guidanceEn: 'Chant 108 times at twilight using a sandalwood mala.',
      guidanceHi: 'संध्याकाल में 108 बार जप करें।',
      daanEn: 'Feed birds and stray dogs; donate coconuts or blue clothes on Saturdays.',
      daanHi: 'पक्षियों को सप्तधान्य डालें और शनिवार को नारियल बहते जल में प्रवाहित करें।'
    },
    Ketu: {
      mantra: 'ॐ स्रां स्रीं स्रौं सः केतवे नमः',
      guidanceEn: 'Chant 108 times early morning with devotion to Lord Ganesha.',
      guidanceHi: 'प्रातःकाल भगवान श्री गणेश का ध्यान करते हुए 108 बार जप करें।',
      daanEn: 'Feed brown/black dogs; donate two-tone blankets or sesame seeds.',
      daanHi: 'दो-रंगी कंबल, तिल, या आवारा कुत्तों को रोटी खिलाएं।'
    }
  };

  const remediesMap: Record<number, {
    summaryEn: string;
    summaryHi: string;
    behavioralEn: string;
    behavioralHi: string;
    spiritualEn: string;
    spiritualHi: string;
    cautionEn: string;
    cautionHi: string;
  }> = {
    1: {
      summaryEn: 'Sun alignment, morning posture discipline & truth-centric self-expression.',
      summaryHi: 'सूर्य अर्घ्य, प्रातःकालीन शारीरिक अनुशासन और आत्म-विश्वास का संवर्धन।',
      behavioralEn: 'Practice daily morning Surya Namaskar or brisk walks under early sunlight. Maintain rigorous posture, punctuality in promises, and commit to personal hygiene and physical vitality.',
      behavioralHi: 'प्रातःकाल सूर्योदय के समय सूर्य नमस्कार अथवा खुली धूप में टहलें। अपने वचनों पर अडिग रहें, शारीरिक स्वास्थ्य व स्फूर्ति का ध्यान रखें और दिनचर्या में अनुशासन बनाए रखें।',
      spiritualEn: 'Chant the Gayatri Mantra or Aditya Hridayam Stotram daily facing East. Offer fresh water (Arghya) with red flowers or a pinch of Kumkum to the rising Sun.',
      spiritualHi: 'प्रतिदिन पूर्व दिशा की ओर मुख करके गायत्री मंत्र अथवा आदित्य हृदय स्तोत्र का पाठ करें। तांबे के पात्र से उगते सूर्य को जल व रोली मिलाकर अर्घ्य दें।',
      cautionEn: 'Avoid arrogance, chronic lethargy, suppressing physical symptoms, or indulging in erratic sleep cycles.',
      cautionHi: 'अहंकार, आलस्य, शारीरिक लक्षणों की अनदेखी और अनियमित दिनचर्या से पूर्णतः बचें।'
    },
    2: {
      summaryEn: 'Truthful satvik speech, systematic liquid budgeting & cow service.',
      summaryHi: 'सत्य व मधुर वाणी, नियमित वित्तीय बचत और गोसेवा।',
      behavioralEn: 'Cultivate mindful, sweet and truthful speech (Vak-Shuddhi). Set aside a disciplined 15-20% of income into fixed savings before spending. Eat fresh, warm, satvik meals with family.',
      behavioralHi: 'सदा सत्य, विनम्र और मधुर वाणी का प्रयोग करें। अपनी आय का कम से कम 15-20% हिस्सा नियमित बचत में रखें। परिवार के साथ बैठकर सात्विक व ताजा भोजन ग्रहण करें।',
      spiritualEn: 'Recite Sri Suktam or Kanakadhara Stotram on Fridays. Feed fresh green fodder or jaggery/rotis to cows regularly to fortify family prosperity.',
      spiritualHi: 'शुक्रवार को श्री सूक्त या कनकधारा स्तोत्र का पाठ करें। गाय को नियमित रूप से हरा चारा, गुड़ अथवा ताजी रोटी खिलाएं, जिससे धन-धान्य में बरकत बनी रहे।',
      cautionEn: 'Avoid using harsh, sarcastic, or abusive words; shun speculative quick-rich schemes and excessive dining out.',
      cautionHi: 'कटु, व्यंग्यात्मक व अभद्र वाणी से बचें। जुए, सट्टे या रातों-रात अमीर बनने के प्रलोभनों से दूर रहें।'
    },
    3: {
      summaryEn: 'Constructive courage, writing/communication discipline & brotherly support.',
      summaryHi: 'रचनात्मक पराक्रम, संवाद कौशल का विकास और छोटे भाइयों का सहयोग।',
      behavioralEn: 'Channel nervous energy into structured writing, skill upgrades, or regular fitness training. Actively support and encourage younger siblings or team members.',
      behavioralHi: 'अपनी ऊर्जा को रचनात्मक लेखन, नए कौशल सीखने अथवा नियमित व्यायाम में लगाएं। छोटे भाई-बहनों और सहकर्मियों का मनोबल बढ़ाएं व उनकी सहायता करें।',
      spiritualEn: 'Chant Hanuman Chalisa or Kartikeya Gayatri every Tuesday. Invoke Lord Rama’s fortitude during daunting endeavors.',
      spiritualHi: 'प्रत्येक मंगलवार को हनुमान चालीसा अथवा कार्तिकेय गायत्री का पाठ करें। कठिन कार्यों के आरंभ में श्री राम और हनुमान जी का स्मरण करें।',
      cautionEn: 'Avoid impulsive conflicts, driving aggressively, spreading unverified rumors, or doubting your inherent capability.',
      cautionHi: 'अनावश्यक वाद-विवाद, तेज ड्राइविंग, अफवाहों को बढ़ावा देने और स्वयं की क्षमताओं पर अविश्वास करने से बचें।'
    },
    4: {
      summaryEn: 'Maternal devotion, clutter-free domestic sanctuary & water element reverence.',
      summaryHi: 'मातृ सेवा, घर में स्वच्छता व शांति, और जल तत्व का सम्मान।',
      behavioralEn: 'Touch mother’s feet or seek maternal blessings regularly. Maintain a calm, clutter-free living environment. Keep the North-East zone of the house clean and open.',
      behavioralHi: 'प्रतिदिन माता के चरण स्पर्श कर आशीर्वाद लें। घर को स्वच्छ, व्यवस्थित और शांत रखें। घर के ईशान कोण (उत्तर-पूर्व) को सदा साफ व हल्का रखें।',
      spiritualEn: 'Recite the Chandra Beej Mantra ("Om Shram Shreem Shroum Sah Chandraya Namah") on Mondays or chant Sri Lalita Sahasranama for profound domestic bliss.',
      spiritualHi: 'सोमवार को चंद्र बीज मंत्र का जप करें अथवा घर में सुख-शांति हेतु ललिता सहस्रनाम या दुर्गा सप्तशती का पाठ करें।',
      cautionEn: 'Avoid bringing corporate grievances into the family dining table, harboring domestic resentment, or disrespecting elderly women.',
      cautionHi: 'घर में बाहरी तनाव न लाएं, पारिवारिक सदस्यों के प्रति मनमुटाव न पालें और मातृ-तुल्य स्त्रियों का कभी अनादर न करें।'
    },
    5: {
      summaryEn: 'Continuous learning, mentoring, ethical investments & Saraswati/Jupiter sadhana.',
      summaryHi: 'सतत स्वाध्याय, ज्ञान का दान, नैतिक निवेश और मां सरस्वती की आराधना।',
      behavioralEn: 'Engage in daily study of philosophy or sacred literature. Mentor students or assist underprivileged children with education. Maintain strict ethics in portfolio investments.',
      behavioralHi: 'प्रतिदिन कुछ समय अध्ययन, चिंतन और स्वाध्याय में लगाएं। निर्धन विद्यार्थियों की शिक्षा में सहयोग करें और निवेश में सदा पारदर्शी व नैतिक मार्ग अपनाएं।',
      spiritualEn: 'Chant Saraswati Stotram or the Brihaspati Gayatri. Perform Gayatri Japa during the tranquil morning dawn (Brahma Muhurta).',
      spiritualHi: 'मां सरस्वती के मंत्रों का जप करें अथवा बृहस्पति गायत्री का पाठ करें। प्रातः ब्रह्ममुहूर्त में गायत्री मंत्र का 108 बार जप परम कल्याणकारी है।',
      cautionEn: 'Avoid intellectual snobbery, impatience with children, reckless financial speculation, and taking karmic shortcuts.',
      cautionHi: 'ज्ञान के अहंकार से बचें, बच्चों के प्रति अत्यधिक कठोर न हों और अनैतिक अथवा सट्टा-संबंधी निवेशों से दूर रहें।'
    },
    6: {
      summaryEn: 'Disciplined lifestyle, conflict de-escalation & service to animals/underprivileged.',
      summaryHi: 'संतुलित दिनचर्या, विवादों से मुक्ति और असहाय जीवों की सेवा।',
      behavioralEn: 'Stick to an unwavering sleep and meal schedule. Manage debt proactively by repaying loans before maturity. Resolve workplace tensions with diplomatic tact rather than rage.',
      behavioralHi: 'नियमित समय पर सोने व भोजन करने की आदत डालें। कर्जों को समय से पूर्व चुकाने का प्रयास करें और कार्यस्थल पर क्रोध के स्थान पर कूटनीति से काम लें।',
      spiritualEn: 'Recite Sudarshana Ashtakam, Mahamrityunjaya Mantra, or Kaal Bhairav Ashtakam. Feed stray dogs on Saturdays to neutralize adversarial energies.',
      spiritualHi: 'सुदर्शन अष्टकम या महामृत्युंजय मंत्र का जप करें। शनिवार को बेसहारा श्वानों (कुत्तों) को भोजन कराएं, जिससे शत्रु व रोग शांत होते हैं।',
      cautionEn: 'Avoid taking unnecessary consumer debt, entering petty legal entanglements, and ignoring digestive or gut health warnings.',
      cautionHi: 'बिना आवश्यकता के नया कर्ज न लें, व्यर्थ के मुकदमों व विवादों से दूर रहें और पेट के स्वास्थ्य को कभी नजरअंदाज न करें।'
    },
    7: {
      summaryEn: 'Empathic partnership listening, transparent contracts & Shiva-Parvati harmony.',
      summaryHi: 'जीवनसाथी के प्रति संवेदनशीलता, पारदर्शी साझेदारी और शिव-पार्वती पूजन।',
      behavioralEn: 'Practice active, compassionate listening with your spouse or business co-founders. Keep business accounts crystal-clear. Honor agreements with unflinching integrity.',
      behavioralHi: 'जीवनसाथी अथवा साझेदारों की बातों को धैर्यपूर्वक सुनें। व्यापारिक व आर्थिक लेन-देन में पूर्ण पारदर्शिता रखें और अपने वादों को निभाएं।',
      spiritualEn: 'Perform joint Shiva-Parvati Puja or chant the Shukra Beej Mantra ("Om Dram Dreem Droum Sah Shukraya Namah"). Offer fragrant white flowers at a temple on Fridays.',
      spiritualHi: 'शुक्रवार को शिव-पार्वती का संयुक्त पूजन करें अथवा शुक्र बीज मंत्र का जप करें। मंदिर में सुगंधित सफेद पुष्प या मिश्री अर्पित करें।',
      cautionEn: 'Avoid taking your partner for granted, projecting personal insecurities into the relationship, or signing unvetted contracts.',
      cautionHi: 'जीवनसाथी की उपेक्षा न करें, अपने संदेहों को रिश्ते पर न थोपें और बिना जांच-परख के किसी अनुबंध पर हस्ताक्षर न करें।'
    },
    8: {
      summaryEn: 'Breathwork/Pranayama, emotional resilience, crisis calm & Mahamrityunjaya Japa.',
      summaryHi: 'प्राणायाम, संकट में धैर्य, आत्म-चिंतन और महामृत्युंजय जप।',
      behavioralEn: 'Practice deep Pranayama (Anulom-Vilom / Bhramari) to steady the central nervous system. Maintain full compliance in taxes, estate, and insurance. Accept life transitions with grace.',
      behavioralHi: 'प्रतिदिन अनुलोम-विलोम व ध्यान करें जिससे मन शांत रहे। बीमा, टैक्स व पैतृक मामलों में नियमबद्ध रहें और जीवन के परिवर्तनों को सहजता से स्वीकारें।',
      spiritualEn: 'Recite the Mahamrityunjaya Mantra 108 times, or chant Om Namah Shivaya. Donate black sesame seeds, iron vessels, or mustard oil on Saturdays.',
      spiritualHi: 'नियमित रूप से 108 बार महामृत्युंजय मंत्र का जप करें। शनिवार को काले तिल, सरसों का तेल अथवा लोहे की वस्तु का दान करें।',
      cautionEn: 'Avoid driving rashly, keeping hazardous secrets, indulging in manipulative psychological games, or exploring dubious occult shortcuts.',
      cautionHi: 'तेज गति से वाहन न चलाएं, अनैतिक रहस्य न रखें और नकारात्मक तंत्र-मंत्र अथवा अंधविश्वास से पूरी तरह दूर रहें।'
    },
    9: {
      summaryEn: 'Reverence to mentors/father, dharmic philanthropy & Vishnu Sahasranama.',
      summaryHi: 'गुरु व पिता का सम्मान, धर्म-परायणता और श्री विष्णु सहस्रनाम पाठ।',
      behavioralEn: 'Seek blessings from your father, spiritual mentors, and teachers. Undertake pilgrimage journeys to holy sites. Dedicate a portion of your earnings to charitable educational funds.',
      behavioralHi: 'पिता व गुरुजनों का आदर करें और उनका मार्गदर्शन लें। समय-समय पर तीर्थ यात्रा करें और अपनी कमाई का कुछ अंश सुपात्र विद्यार्थियों की शिक्षा में लगाएं।',
      spiritualEn: 'Recite Vishnu Sahasranama Stotram on Thursdays. Water a sacred Peepal or Tulsi tree on Thursday mornings without touching on Sundays.',
      spiritualHi: 'गुरुवार को श्री विष्णु सहस्रनाम का पाठ करें अथवा बृहस्पति मंत्र का जप करें। गुरुवार के दिन तुलसी व पीपल में जल अर्पित करें।',
      cautionEn: 'Avoid disrespecting traditions, speaking cynicism against spiritual elders, or engaging in moral hypocrisy.',
      cautionHi: 'गुरु-निंदा, धर्म व संस्कृति का उपहास, और कथनी व करनी में अंतर रखने से बचें।'
    },
    10: {
      summaryEn: 'Professional excellence, ethical leadership, public duty & Saturn/Sun balancing.',
      summaryHi: 'कर्तव्यनिष्ठा, कार्यक्षेत्र में पारदर्शिता, और सूर्य-शनि संतुलन।',
      behavioralEn: 'Lead with quiet humility and uncompromising work ethics. Treat subordinates and service workers with dignity. Focus on long-term mastery rather than fleeting political gains.',
      behavioralHi: 'कार्यक्षेत्र में निष्ठा, ईमानदारी और विनम्रता से काम करें। अपने अधीनस्थों व कर्मचारियों का सम्मान करें। पद की शक्ति का दुरुपयोग न करें।',
      spiritualEn: 'Chant Surya Beej Mantra at dawn, or recite Dasaratha Shani Stotram on Saturdays depending on whether the house is ruled by Sun, Mars, or Saturn.',
      spiritualHi: 'प्रातः सूर्य को अर्घ्य दें और शनिवार को दशरथ कृत शनि स्तोत्र का पाठ करें। कार्यस्थल पर न्यायप्रिय व सत्यवादी आचरण रखें।',
      cautionEn: 'Avoid office politics, abusing positional authority over helpless staff, and sacrificing family health for workaholism.',
      cautionHi: 'कार्यस्थल पर गुटबाजी, कर्मचारियों का शोषण, और काम के चक्कर में स्वास्थ्य व परिवार की उपेक्षा करने से बचें।'
    },
    11: {
      summaryEn: 'Ethical networking, community philanthropy, Kubera/Lakshmi mantra & goal clarity.',
      summaryHi: 'सात्विक मित्रों का संग, समाज कल्याण, श्री कुबेर-लक्ष्मी पूजन और स्पष्ट लक्ष्य।',
      behavioralEn: 'Cultivate meaningful relationships with inspiring, ethical peers. Share windfall gains with community welfare causes. Review your financial milestones quarterly.',
      behavioralHi: 'सज्जन व प्रेरणादायी मित्रों की संगति करें। लाभ होने पर उसका एक अंश समाज-सेवा में लगाएं। अपने आर्थिक व व्यावसायिक लक्ष्यों की नियमित समीक्षा करें।',
      spiritualEn: 'Chant Om Shreem Maha Lakshmiyei Namaha or Om Kuberaya Namaha on Thursdays/Fridays. Support animal shelters or collective community kitchens (Langar).',
      spiritualHi: 'गुरुवार या शुक्रवार को ॐ श्रीं महालक्ष्म्यै नमः अथवा कुबेर मंत्र का जप करें। अन्नक्षेत्र या लंगर में यथाशक्ति दान करें।',
      cautionEn: 'Avoid greed-driven associations, spending beyond your means to impress social circles, and taking predatory financial shortcuts.',
      cautionHi: 'लोभ में आकर गलत लोगों से मित्रता न करें, दिखावे में व्यर्थ खर्च न करें और किसी के हक का धन न छीनें।'
    },
    12: {
      summaryEn: 'Mindful solitude, anonymous charity, restful sleep hygiene & Moksha meditation.',
      summaryHi: 'एकांत साधना, गुप्त दान, अनिद्रा निवारण और मोक्ष प्रदायक ध्यान।',
      behavioralEn: 'Set a strict digital sunset 1 hour before bedtime to foster restorative sleep. Practice silent meditation or introspection for 20 minutes daily. Donate anonymously without seeking fame.',
      behavioralHi: 'सोने से 1 घंटा पूर्व फोन/स्क्रीन से दूर रहें। प्रतिदिन 20 मिनट मौन व ध्यान का अभ्यास करें। असहायों की गुप्त रूप से सहायता करें, दिखावे से बचें।',
      spiritualEn: 'Chant the 12-syllable mantra "Om Namo Bhagavate Vasudevaya" or perform Shiva Dhyana before sleep. Feed migratory birds or fishes near rivers/lakes.',
      spiritualHi: 'सोने से पूर्व "ॐ नमो भगवते वासुदेवाय" अथवा "ॐ नमः शिवाय" का शांत मन से स्मरण करें। पक्षियों व मछलियों को दाना डालें।',
      cautionEn: 'Avoid late-night overthinking, excessive solitary escapism, indulging in intoxicants, and trusting dubious foreign schemes.',
      cautionHi: 'देर रात तक जागने, नशे की प्रवृत्ति, अत्यधिक एकाकीपन और विदेशों से जुड़े संदिग्ध लेन-देन से बचें।'
    }
  };

  const item = remediesMap[houseNumber] || remediesMap[1];

  const planetInfo = mantrasByPlanet[signLord] || mantrasByPlanet['Jupiter'];
  const mantra = planetInfo.mantra;
  const chantingGuidance = isHi ? planetInfo.guidanceHi : planetInfo.guidanceEn;
  const daan = isHi ? planetInfo.daanHi : planetInfo.daanEn;

  let dashaSynergyNote: string | undefined;
  if (dashaActive && currentMDPlanet) {
    dashaSynergyNote = isHi
      ? `वर्तमान में इस भाव के स्वामी (${signLord}) की दशा सक्रिय है, अतः इन उपायों का प्रभाव कई गुना तीव्र व प्रभावशाली रहेगा।`
      : `The Mahadasha/Antardasha of this house lord (${signLord}) is actively running, significantly magnifying the responsiveness of these remedies.`;
  }

  const gemstoneCaution = isHi
    ? `रत्न धारण से पूर्व भावेश ${signLord} का अपनी लग्न कुंडली अनुसार शुभत्व (Functional Benefic) जांचें। त्रिक भावों (६, ८, १२) के स्वामी का रत्न बिना गहन परीक्षण के कदापि न पहनें।`
    : `Verify whether Lord ${signLord} is a functional benefic for your specific Ascendant before wearing any gemstone. Avoid gems of 6th, 8th, or 12th lords without qualified guidance.`;

  return {
    quickSummary: isHi ? item.summaryHi : item.summaryEn,
    behavioral: isHi ? item.behavioralHi : item.behavioralEn,
    spiritual: isHi ? item.spiritualHi : item.spiritualEn,
    caution: isHi ? item.cautionHi : item.cautionEn,
    targetPlanet: signLord,
    mantra,
    chantingGuidance,
    daan,
    dashaSynergyNote,
    gemstoneCaution
  };
};

interface HouseInspectorViewProps {
  selectedHouseNumber: number;
  onSelectHouse: (houseNumber: number) => void;
  selectedPlanet: PlanetPosition | null;
  onSelectPlanet: (planet: PlanetPosition | null) => void;
  chartData: KundaliChartData;
  kundali: CompleteKundali;
  lang?: Language;
  layoutMode?: 'all' | 'split-top' | 'split-bottom';
}

export const HouseInspectorView: React.FC<HouseInspectorViewProps> = ({
  selectedHouseNumber,
  onSelectHouse,
  selectedPlanet,
  onSelectPlanet,
  chartData,
  kundali,
  lang = 'en',
  layoutMode = 'all'
}) => {
  const activeLang = (lang as Language) || 'en';
  const isHi = activeLang === 'hi';

  // Preferences sync with SettingsModal
  const [defaultViewPref, setDefaultViewPref] = useState<'collapsed' | 'expanded'>(() => {
    return (localStorage.getItem('astro_inspector_default_view') as 'collapsed' | 'expanded') || 'collapsed';
  });


  const [isDignityTableOpen, setIsDignityTableOpen] = useState(() => defaultViewPref === 'expanded');
  const [isSynthesisOpen, setIsSynthesisOpen] = useState(() => defaultViewPref === 'expanded');
  const [isBusinessOpen, setIsBusinessOpen] = useState(() => defaultViewPref === 'expanded');
  const [isYogasOpen, setIsYogasOpen] = useState(() => defaultViewPref === 'expanded');
  const [isBhavaDeepDiveOpen, setIsBhavaDeepDiveOpen] = useState(() => defaultViewPref === 'expanded');
  const [isAshtakvargaOpen, setIsAshtakvargaOpen] = useState(() => defaultViewPref === 'expanded');
  const [isGocharOpen, setIsGocharOpen] = useState(() => defaultViewPref === 'expanded');
  const [isRemediesOpen, setIsRemediesOpen] = useState(() => defaultViewPref === 'expanded');

  useEffect(() => {
    const handlePrefChange = () => {
      const pref = (localStorage.getItem('astro_inspector_default_view') as 'collapsed' | 'expanded') || 'collapsed';
      setDefaultViewPref(pref);
      const isOpen = pref === 'expanded';
      setIsDignityTableOpen(isOpen);
      setIsSynthesisOpen(isOpen);
      setIsBusinessOpen(isOpen);
      setIsYogasOpen(isOpen);
      setIsBhavaDeepDiveOpen(isOpen);
      setIsAshtakvargaOpen(isOpen);
      setIsGocharOpen(isOpen);
      setIsRemediesOpen(isOpen);
    };
    window.addEventListener('astro_inspector_view_pref_changed', handlePrefChange);
    return () => window.removeEventListener('astro_inspector_view_pref_changed', handlePrefChange);
  }, []);

  const allOpen = isDignityTableOpen && isSynthesisOpen && isBusinessOpen && isYogasOpen && isBhavaDeepDiveOpen && isAshtakvargaOpen && isGocharOpen && isRemediesOpen;
  const toggleAll = () => {
    const nextState = !allOpen;
    setIsDignityTableOpen(nextState);
    setIsSynthesisOpen(nextState);
    setIsBusinessOpen(nextState);
    setIsYogasOpen(nextState);
    setIsBhavaDeepDiveOpen(nextState);
    setIsAshtakvargaOpen(nextState);
    setIsGocharOpen(nextState);
    setIsRemediesOpen(nextState);
  };

  // Live dynamic recalculation state: updates dynamically whenever opened or house changes
  const [transitTimestamp, setTransitTimestamp] = useState<Date>(() => new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeHoverPlanet, setActiveHoverPlanet] = useState<{
    planet: PlanetPosition;
    rolesEn: string[];
    rolesHi: string[];
    dignityLabel: string;
    funcNature: string;
    shadbalaRatio?: number;
    shadbalaCategory?: string;
    shadbalaRupas?: number;
    x: number;
    y: number;
  } | null>(null);

  // Recalculate dynamic transit time on mount and house switch
  useEffect(() => {
    setTransitTimestamp(new Date());
  }, [selectedHouseNumber]);

  const handleRefreshTransit = () => {
    setIsRefreshing(true);
    setTransitTimestamp(new Date());
    setTimeout(() => setIsRefreshing(false), 400);
  };

  // Precompute Shadbala & Dasha timings
  const shadbalaData = useMemo(() => {
    if (kundali.shadbala && kundali.shadbala.planets && kundali.shadbala.planets.length > 0) {
      return kundali.shadbala;
    }
    return calculateShadbala(chartData.planets, kundali.houses, {
      birthDate: getBirthUtcDate(kundali.birthDetails),
      latitude: kundali.birthDetails.latitude,
      longitude: kundali.birthDetails.longitude
    });
  }, [kundali, chartData]);

  // Hover Tooltips Inspector ON/OFF toggle state
  const [tableTooltipsEnabled, setTableTooltipsEnabled] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('astro_table_hover_tooltips_enabled');
      return stored !== 'false';
    } catch {
      return true;
    }
  });

  const toggleTableTooltips = () => {
    setTableTooltipsEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('astro_table_hover_tooltips_enabled', String(next));
      } catch {}
      return next;
    });
  };

  // Enhanced Dignity Data Map
  const enhancedDignityList = useMemo(() => {
    return getEnhancedPlanetaryDignityList(kundali, activeLang);
  }, [kundali, activeLang]);

  const enhancedDignityMap = useMemo(() => {
    const map: Record<string, EnhancedPlanetaryDignity> = {};
    enhancedDignityList.forEach(item => {
      map[item.planet] = item;
    });
    return map;
  }, [enhancedDignityList]);

  // Transit Periods Map for Live Gochar
  const transitPeriodsMap = useMemo(() => {
    const planets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
    const map: Record<string, PlanetaryTransitPeriod> = {};
    planets.forEach(p => {
      try {
        map[p] = calculatePlanetaryTransitPeriod(p, transitTimestamp);
      } catch {}
    });
    return map;
  }, [transitTimestamp]);

  const currentMD = useMemo(() => {
    return kundali.dashas?.find(d => d.isCurrent) || kundali.dashas?.[0];
  }, [kundali.dashas]);

  const currentAD = useMemo(() => {
    return currentMD?.antardashas?.find(a => a.isCurrent) || currentMD?.antardashas?.[0];
  }, [currentMD]);

  const houseAnalysis: ComprehensiveHouseAnalysis | null = getComprehensiveHouseCondition(
    selectedHouseNumber,
    chartData,
    kundali,
    transitTimestamp
  );

  // Calculate upcoming planetary ingress specifically for this selected house's Rashi
  const nextTransitsForSelectedHouse: HouseNextTransitInfo = useMemo(() => {
    const targetSignIndex = houseAnalysis?.signIndex ?? ((chartData.ascendantIndex + selectedHouseNumber - 1) % 12);
    return calculateNextTransitsForSign(targetSignIndex, transitTimestamp);
  }, [houseAnalysis?.signIndex, chartData.ascendantIndex, selectedHouseNumber, transitTimestamp]);

  if (!houseAnalysis) {
    return (
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-6 text-center text-xs text-astro-secondary-text">
        Select a house to inspect conditions and planetary dynamics.
      </div>
    );
  }

  const {
    houseNumber,
    sign,
    signLord,
    signLordPosition,
    sarvashtakvargaPoints,
    signification,
    occupants,
    aspects,
    parivartana,
    yogas = [],
    houseStrengthVerdict,
    ashtakvargaEvaluation,
    gocharEvaluation
  } = houseAnalysis;

  const ascSign = ZODIAC_SIGNS[chartData.ascendantIndex ?? 0];

  // Real Net Impact Synthesis & Synthesized Upaya calculation
  const netSynthesis = useMemo(() => {
    return computeHolisticHouseNetSynthesis(
      houseNumber,
      sign,
      signLord,
      signLordPosition,
      sarvashtakvargaPoints,
      occupants,
      aspects,
      shadbalaData?.planets,
      currentMD,
      currentAD,
      isHi,
      ascSign,
      parivartana
    );
  }, [houseNumber, sign, signLord, signLordPosition, sarvashtakvargaPoints, occupants, aspects, shadbalaData, currentMD, currentAD, isHi, ascSign, parivartana]);

  const remedies = useMemo(() => {
    return computeSynthesizedUpaya(
      houseNumber,
      sign,
      signLord,
      occupants,
      aspects,
      netSynthesis.netScore,
      currentMD?.planet,
      currentAD?.planet,
      sarvashtakvargaPoints,
      isHi
    );
  }, [houseNumber, sign, signLord, occupants, aspects, netSynthesis.netScore, currentMD, currentAD, sarvashtakvargaPoints, isHi]);

  // Unified Layman Practical Life Summary & Actionable Takeaways
  const laymanPracticalSummary = useMemo(() => {
    const lordPlanet = chartData.planets.find(p => p.name === signLord);
    const lordEnh = enhancedDignityMap[signLord];
    const isLordStrong = (lordEnh?.shadbala?.ratio ?? 1.0) >= 1.0;
    const isLordWellPlaced = lordPlanet && [1, 2, 4, 5, 7, 9, 10, 11].includes(lordPlanet.house);
    const hasSavCushion = (sarvashtakvargaPoints ?? 28) >= 28;
    const hasBeneficInfluence = aspects.some(a => ['Jupiter', 'Venus', 'Moon', 'Mercury'].includes(a.planet)) || occupants.some(o => ['Jupiter', 'Venus', 'Moon', 'Mercury'].includes(o.name));
    const hasMaleficStress = aspects.some(a => ['Saturn', 'Mars', 'Rahu', 'Ketu'].includes(a.planet)) || (lordEnh?.dignityLabelEn === 'Debilitated' || lordEnh?.dignityLabelEn === 'Enemy Sign');

    // 1. Real-Life Impact
    let impactEn = '';
    let impactHi = '';
    const houseThemes = getHouseThemes(houseNumber, activeLang);
    const themeStr = houseThemes.slice(0, 3).join(', ');
    if (isLordStrong && isLordWellPlaced && hasSavCushion) {
      impactEn = `This house is an active powerhouse in your chart. Its core life themes (${themeStr}) will flow naturally and bring tangible growth, consistent recognition, and dependable support when you invest regular effort.`;
      impactHi = `यह भाव आपकी कुंडली का मजबूत आधार स्तंभ है। इसके मुख्य विषय (${themeStr}) जीवन में सहजता से फलेंगे और आपके सतत प्रयासों से निरंतर उन्नति, सम्मान व सफलता प्रदान करेंगे।`;
    } else if (isLordStrong && !isLordWellPlaced) {
      impactEn = `You possess strong internal drive and talent in these life areas (${themeStr}), but because the governing lord is placed in a transformative house, substantial breakthroughs arrive following self-reinvention, relocation, or overcoming initial testing phases.`;
      impactHi = `आप में इस क्षेत्र (${themeStr}) की प्रबल आंतरिक क्षमता है, परंतु भावेश के रूपांतरण भाव में होने से वास्तविक सफलता आत्म-सुधार, स्थान परिवर्तन या प्रारंभिक संघर्ष के बाद सुदृढ़ होगी।`;
    } else if (!isLordStrong && hasSavCushion) {
      impactEn = `While the primary governing energy requires mindful nurturing and steady habits, the solid environmental support (${sarvashtakvargaPoints ?? 28} SAV points) ensures that timely external assistance, helpful mentors, and lucky breaks will cushion your path.`;
      impactHi = `यद्यपि भावेश को नियमित संबल और अनुशासित दिनचर्या की आवश्यकता है, किंतु उत्तम अष्टकवर्ग (${sarvashtakvargaPoints ?? 28} बिंदु) के कारण आपको सही समय पर मित्रों, मार्गदर्शकों व अनुकूल परिस्थितियों का भरपूर सहयोग मिलता रहेगा।`;
    } else {
      impactEn = `This is an active self-mastery and development zone in your life. Tangible progress is built gradually through systematic daily routines, emotional patience, and avoiding rushed or high-risk decisions.`;
      impactHi = `यह भाव आपके जीवन का आत्म-विकास और धैर्य का क्षेत्र है। यहाँ स्थायी प्रगति नियमित अनुशासन, शांत मनोदशा और बिना जल्दबाजी के विवेकपूर्ण निर्णय लेने से निर्मित होगी।`;
    }

    // 2. Challenge & Growth Area
    let challengeEn = '';
    let challengeHi = '';
    if (hasMaleficStress) {
      challengeEn = `Because this house experiences tough planetary pressures or friction, you may encounter initial delays, self-doubt, or demanding workloads. Remember that early friction is building long-term resilience—avoid impulsive pivots.`;
      challengeHi = `क्रूर ग्रह प्रभाव या संघर्षकारी दबाव के कारण कार्यों में आरंभिक रुकावट, संशय या अधिक श्रम की आवश्यकता हो सकती है। प्रारंभिक देरी को असफलता न समझें, धैर्यपूर्वक निरंतर प्रयास जारी रखें।`;
    } else if (occupants.length > 1) {
      const occupantNames = occupants.map(o => getPlanetFullName(o.name as any, activeLang)).join(', ');
      challengeEn = `With multiple planetary energies converging here (${occupants.map(o => o.name).join(', ')}), priorities may occasionally pull in opposite directions. Establishing clear boundaries and focusing on one primary milestone at a time will prevent scattered energy.`;
      challengeHi = `एक से अधिक ग्रहों की उपस्थिति (${occupantNames}) से प्राथमिकताओं में भटकाव संभव है। एक समय में एक ही लक्ष्य पर ध्यान केंद्रित करना और सीमाएं तय करना आपकी मुख्य कुंजी है।`;
    } else {
      challengeEn = `Energy operates smoothly here with minimal friction. The primary caution is avoiding complacency or taking favorable circumstances for granted during peaceful periods.`;
      challengeHi = `यहाँ ऊर्जा का प्रवाह अत्यंत संतुलित है। मुख्य सावधानी यही है कि सब कुछ सुचारू चलने पर लापरवाही या अति-आत्मविश्वास से बचें।`;
    }

    // 3. Actionable Golden Rule
    let adviceEn = '';
    let adviceHi = '';
    if ([1, 5, 9].includes(houseNumber)) {
      adviceEn = `Listen closely to your inner moral compass, dedicate time each week to higher learning or skill mastery, and lead with composed humility rather than forceful ego.`;
      adviceHi = `अपने आत्म-विवेक और धर्म पर अडिग रहें, निरंतर ज्ञानार्जन व कौशल विकास करें और अहंकार के बजाय सात्विक विनम्रता से नेतृत्व करें।`;
    } else if ([2, 10, 11].includes(houseNumber)) {
      adviceEn = `Systematize your financial savings, uphold strict ethical transparency in all professional and financial deals, and actively nurture genuine win-win relationships in your network.`;
      adviceHi = `वित्तीय बचत को नियमबद्ध करें, पेशेवर व आर्थिक लेन-देन में पूर्ण पारदर्शिता रखें और अपने संपर्कों के साथ परस्पर हितकारी संबंध बनाएं।`;
    } else if ([4, 7].includes(houseNumber)) {
      adviceEn = `Practice active, non-judgmental listening in your close partnerships. Cultivating a peaceful, supportive emotional sanctuary at home will instantly supercharge your focus in external work.`;
      adviceHi = `व्यक्तिगत व व्यावसायिक संबंधों में खुले दिल और धैर्य से संवाद करें। घर-परिवार में सौहार्द बनाए रखना ही आपकी बाह्य प्रगति को गति देगा।`;
    } else {
      adviceEn = `Incorporate selfless charity into your monthly routine, resolve conflicts early through diplomacy rather than stubborn ego, and maintain regular physical health and mindfulness routines.`;
      adviceHi = `नियमित रूप से सामर्थ्यानुसार सेवा व दान करें, विवादों को शांतिपूर्ण संवाद से सुलझाएं और स्वास्थ्य व ध्यान को दिनचर्या में सर्वोच्च प्राथमिकता दें।`;
    }

    return { impactEn, impactHi, challengeEn, challengeHi, adviceEn, adviceHi };
  }, [signLord, chartData.planets, enhancedDignityMap, sarvashtakvargaPoints, aspects, occupants, houseNumber, activeLang]);

  // Find dasha timing for yoga planets (Filter for current and upcoming) - Hierarchical
  const getYogaDashaPeriodsHierarchical = (planetsToFind: string[]) => {
    if (!kundali.dashas) return [];
    
    const hierarchicalResults: any[] = [];
    
    kundali.dashas.forEach(md => {
      const isMdRelevant = planetsToFind.includes(md.planet);
      const relevantAds: any[] = [];

      if (md.antardashas) {
        md.antardashas.forEach(ad => {
          const isAdRelevant = planetsToFind.includes(ad.planet);
          const relevantPds: any[] = [];

          if (ad.antardashas) {
            ad.antardashas.forEach(pd => {
              if (planetsToFind.includes(pd.planet)) {
                relevantPds.push({
                  planet: pd.planet,
                  type: 'Pratyantar',
                  start: pd.startDate,
                  end: pd.endDate,
                  isCurrent: pd.isCurrent
                });
              }
            });
          }

          if (isAdRelevant || relevantPds.length > 0) {
            relevantAds.push({
              planet: ad.planet,
              type: 'Antardasha',
              start: ad.startDate,
              end: ad.endDate,
              isCurrent: ad.isCurrent,
              pratyantars: relevantPds
            });
          }
        });
      }

      if (isMdRelevant || relevantAds.length > 0) {
        hierarchicalResults.push({
          planet: md.planet,
          type: 'Mahadasha',
          start: md.startDate,
          end: md.endDate,
          isCurrent: md.isCurrent,
          antardashas: relevantAds
        });
      }
    });
    
    return hierarchicalResults;
  };

  const formatDetailedDate = (dateStr: string) => {
    return formatDate(dateStr);
  };

  // State for collapsible dasha timelines
  const [expandedDashas, setExpandedDashas] = useState<Record<string, boolean>>({});

  const toggleDashaExpand = (key: string) => {
    setExpandedDashas(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const renderYogaCard = (name: string, type: string, description: string, participatingPlanets: string[], isAuspicious: boolean) => {
    const yogaDashas = getYogaDashaPeriodsHierarchical(participatingPlanets);
    
    return (
      <div key={name} className={`p-4 rounded-xl border text-xs flex flex-col gap-3 shadow-sm ${
        isAuspicious
          ? 'bg-emerald-50 border-emerald-200 text-emerald-950'
          : 'bg-astro-secondary-surface border-[#E2DED5] text-[#263633]'
      }`}>
        <div className="flex items-start gap-3">
          <Sparkles className={`w-5 h-5 shrink-0 mt-0.5 ${isAuspicious ? 'text-emerald-700' : 'text-[#52635F]'}`} />
          <div>
            <div className="font-bold mb-1 flex flex-wrap items-center gap-2">
              <span className="text-sm font-serif">{name}</span>
              <span className="text-[10px] font-sans opacity-70 tracking-wide uppercase">({type})</span>
              <span className={`text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-tight ${isAuspicious ? 'bg-emerald-200 text-emerald-800' : 'bg-astro-secondary/20 text-astro-secondary'}`}>
                {isAuspicious ? (isHi ? 'अत्यंत शुभ' : 'Highly Auspicious') : (isHi ? 'कर्मिक' : 'Karmic')}
              </span>
            </div>
            <p className="text-[11px] leading-relaxed opacity-90 font-medium">
              {description}
            </p>
          </div>
        </div>

        {/* Dasha Fructification Section - Hierarchical & Collapsible */}
        {yogaDashas.length > 0 && (
          <div className="pt-3 border-t border-current/15">
            <div className="flex items-center gap-1.5 mb-3 text-[10px] font-bold uppercase tracking-wider">
              <Activity className="w-3.5 h-3.5" />
              <span>{isHi ? 'योग फलित काल (पदानुक्रमित)' : 'Yoga Fructification Periods (Hierarchical)'}</span>
            </div>
            <div className="space-y-3">
              {yogaDashas.map((md, i) => {
                const cardKey = `${name}_md_${i}_${md.planet}`;
                const isCurrentOrActive = Boolean(md.isCurrent) || md.antardashas?.some((ad: any) => ad.isCurrent);
                const isExpanded = expandedDashas[cardKey] !== undefined ? expandedDashas[cardKey] : isCurrentOrActive;

                return (
                  <div key={i} className={`p-3 rounded-xl border transition-all ${md.isCurrent ? 'bg-white border-current/30 shadow-sm' : 'bg-white/30 border-current/10'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[11px] text-current">
                          {getPlanetFullName(md.planet, activeLang)} (MD)
                        </span>
                        {md.isCurrent && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 bg-emerald-600 text-white rounded uppercase tracking-wider">
                            {isHi ? 'वर्तमान' : 'Current'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] font-mono opacity-80">
                          {formatDetailedDate(md.start)} - {formatDetailedDate(md.end)}
                        </span>
                        <button 
                          onClick={() => toggleDashaExpand(cardKey)}
                          className="p-1 hover:bg-current/10 rounded-full transition-colors text-current cursor-pointer"
                          title={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                    
                    {isExpanded && md.antardashas.length > 0 && (
                      <div className="ml-4 pl-3 border-l-2 border-current/10 space-y-2 mt-2 animate-in fade-in slide-in-from-top-1 duration-200">
                        {md.antardashas.map((ad: any, j: number) => (
                          <div key={j} className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <span className={`text-[10px] font-semibold ${ad.isCurrent ? 'text-current font-bold' : 'opacity-70'}`}>
                                  • {getPlanetFullName(ad.planet, activeLang)} (AD)
                                </span>
                                {ad.isCurrent && (
                                  <span className="text-[8px] font-bold px-1 py-0.2 bg-emerald-500 text-white rounded">
                                    {isHi ? 'वर्तमान' : 'Active'}
                                  </span>
                                )}
                              </div>
                              <span className="text-[9px] font-mono opacity-60">
                                {formatDetailedDate(ad.start)} - {formatDetailedDate(ad.end)}
                              </span>
                            </div>
                            
                            {ad.pratyantars.length > 0 && (
                              <div className="ml-4 flex flex-wrap gap-1.5">
                                {ad.pratyantars.map((pd: any, k: number) => (
                                  <div key={k} className={`px-2 py-0.5 rounded-md text-[9px] border font-mono ${pd.isCurrent ? 'bg-current text-white border-current font-bold' : 'bg-white/50 border-current/20 opacity-80'}`}>
                                    {getPlanetShortCode(pd.planet, activeLang)} (PD): {formatDetailedDate(pd.start)} - {formatDetailedDate(pd.end)}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };


  return (
    <div className="w-full">
      {/* Unified Parashari Complete Bhava Condition Assessment (4-Step Diagnostic & Remedial Workflow) */}
      <UnifiedBhavaConditionAssessmentView
        houseNumber={selectedHouseNumber}
        chartData={chartData}
        kundali={kundali}
        lang={activeLang}
        shadbalaData={shadbalaData}
        enhancedDignityMap={enhancedDignityMap}
        onSelectHouse={onSelectHouse}
      />
    </div>
  );
};

